<template>
<div class="chapter">
<div class="inner_page">
<a id="page_43"></a>
<h2 class="ct">THURSDAY AUGUST 11, 1988</h2>
<h2 class="ct1">THE THIRD DAY</h2>
<a id="page_44"></a>
<a id="page_45"></a>
<p class="noindent"><span class="bold"><span class="font1">5:25 A.M.</span></span> Around
            midnight, Sally had wrapped herself in a blanket and curled in the armchair beside the
            bed where Terry lay sleeping. Long after the cafeteria dinner, long after Chris Van
            Allen trundled home to bed, she sat watching Terry, listening to the soft brush of the
            rain at the windows, listening to the repetitive, reassuring <em class="italic">ping</em> of the cardiac monitor. She thought of her own comfortable bed, thought
            of going home and crawling into it. But she knew he might wake in the night and she
            wanted to be there for him if he did. At 2 A.M., a nurse roused her, showed her to the
            vacant room across the hall, laid a fresh gown and robe for her, and turned the covers
            down!</p>
<p class="indent">Sally arranged to have their calls routed to her room, undressed and slid
            between the cool sheets, let her body sink back into the thick, sustaining mattress. Her
            shoulders ached and her hands were stiff with writer’s cramp. She lay back and
            shut her eyes. But she did not sleep.</p>
<p class="indent">She kept thinking of Speaker Charlie O’Donnell and the look on his
            face when he’d entered Terry’s room. There was such haste in his eyes,
            such portent of things to come. She had always felt uneasy around O’Donnell.
            There were smarter men in Congress—shrewder men, too. But O’Donnell was
            the “master of the game.” No one in Washington had more clout—not
            the President, not the chairman of the Joint Chiefs. No one was better
            informed—not the director of the FBI, not Ben Bradlee of the <em class="italic">Post</em>. No one had more stature—not even the chief justice. And no one
            could be more dangerous.</p>
<p class="indent">Charlie O’Donnell could make careers and break them. After thirty
            years on the Hill, his network of informers and his wealth of favors-owed gave him power
            and influence that approached omnipotence. He could move legislation or table it. He
            could advance “corners” or bury them. Maybe he couldn’t pass any
            law he wanted—but he could line up enough votes to defeat any bill,
            even—so they said—the Bill of Rights.</p>
<a id="page_46"></a>
<p class="indent">And now he had come to Terry Fallon’s sick room, carrying a message
            from the President …</p>
<p class="indent">Silently, under the cover of thickening darkness, Sally lay still in her
            bed, thinking of O’Donnell and the days to come. She was stepping over, she knew,
            into the realm of absolute power. She was crossing the threshold. Her eyes slowly
            closed, and behind her lids another iris opened—first in rays of gold and yellow,
            then in a dizzying spiral of white heat that soared and swelled until she was looking
            into a blast of blinding light, looking into the fusion of power, into the very furnace
            of energy that drives the stars. It seared her skin and raced her heart to bursting, it
            roared through her hair, and made her gasp to catch her breath. It was all terror and
            all glory and all being. It was everything she feared. It was all she’d ever
            wanted.</p>
<p class="indent">It was the phone ringing.</p>
<p class="indent">“Yes, Chris?”</p>
<p class="indent">“You’re gonna love this,” he said.</p>
<p class="indent">Sally and Chris talked every morning at five-thirty. He always called her.
            That way, she wouldn’t know where he was sleeping unless he wanted her to. This
            morning, he wanted her to.</p>
<p class="indent">“Ted Wyckoff,” Chris said. “Can you believe
            it?”</p>
<p class="indent">Sally pushed herself up on one elbow. “The guy who works for
            Eastman?”</p>
<p class="indent">“The same.”</p>
<p class="indent">“You’re right. I don’t believe it.”</p>
<p class="indent">Chris was sitting at the white wrought-iron table on the leafy little
            patio behind his townhouse in Georgetown. The first streaks of light were pinking the
            sky, and the early summer morning was heavy with the scent of jasmine. His slinky
            Jamaican houseman, Maurice, swept out the back door in his white linen caftan and put a
            cup of <em class="italic">café au lait</em> and a packet of gold-tipped
            cigarettes on the table before him.</p>
<p class="indent">“He’s snoring away upstairs,” Chris said.
            “Quite a pretty picture.”</p>
<p class="indent">“But he always came on so … straight.”</p>
<p class="indent">“Darling, you simply don’t understand these things,”
            Chris said. “Maybe I just bring out the beast in him” He giggled.
            “I can be such a queen.”</p>
<p class="indent">Sally had to laugh.</p>
<p class="indent">“I’ll give you three guesses what he wanted to talk
            about,” Chris said. “And your first two don’t count.”</p>
<p class="indent">“Terry.”</p>
<a id="page_47"></a>
<p class="indent">“Terry, Terry, only Terry. How he admires him. How he respects him.
            How much he’d love to be a part of the team when Terry is vice
            President.”</p>
<p class="indent">That made her sit up. “You didn’t say anything …
            Chris? Did you?”</p>
<p class="indent">“Sally, honestly. Don’t you think I know when I’m
            being made?” Chris took another sip of coffee. “Now hear this. Wyckoff
            says he’s had it on authority that Terry’s brain damaged from loss of
            blood, incapacitated, dysfunctional—a vegetable.”</p>
<p class="indent">“What? That’s ridiculous.”</p>
<p class="indent">“Maybe. But that’s what the town will be chewing this
            morning with their bacon and eggs. Remember. This is Foggy Bottom.”</p>
<p class="indent">“Who the hell would spread a rumor like that?” Sally said,
            her anger rising.</p>
<p class="indent">“My guess…?” There was a smirk in Chris’s
            voice. “He’s snoring away upstairs.”</p>
<p class="indent">Sally sat a moment, silent. So it had begun. The Eastman team wanted the
            nomination—and if they were going down, they were going down hard. This was the
            opening skirmish, a fire-fight before the main battle. From now until the nomination was
            won or lost, Eastman’s forces would probe Terry’s lines looking for
            weakness. And if they found weakness, they would attack ruthlessly. Sally knew she had
            to move.</p>
<p class="indent">“Bedside press conference?” Chris said.</p>
<p class="indent">“No chance.” Sally swung her legs out of bed and pulled on
            the hospital robe, changing the telephone from ear to ear to keep the conversation
            going. “Doctors and hospitals scare people out of their wits. I don’t want
            the country plastered with pictures of Terry looking like an invalid. We’ve got
            to get out of the sick room before we get in front of the cameras. How soon can you be
            here?”</p>
<p class="indent">“Gee, boss, I was just going upstairs-for a daybreaker.”</p>
<p class="indent">“Twenty minutes.”</p>
<p class="indent">“Yes, <em class="italic">bwana,”</em> Chris said. He
            hung up.</p>
<p class="indent">Sally held down the button on the telephone and looked at her wristwatch.
            It was coming up on 5:30—soon, the network morning news shows would go on the
            air. This was Washington, and rumor was no pastime. It was a game for professionals:
            vicious, wicked, deadly. She punched the 212 area code, then the number of Steve
            Chandler’s private line.</p>
<p class="indent">“How’s your boy?” Chandler said.</p>
<a id="page_48"></a>
<p class="indent">“A-okay.”</p>
<p class="indent">“What about this brain damage thing?”</p>
<p class="indent">So, the word had already hit New York. “Red herring,” Sally
            said.</p>
<p class="indent">“Show me.”</p>
<p class="indent">“We will.”</p>
<p class="indent">“What about the number two spot?”</p>
<p class="indent">“No comment.”</p>
<p class="indent">“O’Donnell went to see him.”</p>
<p class="indent">“Just a courtesy call.”</p>
<p class="indent">Chandler didn’t buy that.</p>
<p class="indent">“Can you give me anything on background?” he said.</p>
<p class="indent">“Not today.”</p>
<p class="indent">“Deep background?”</p>
<p class="indent">“Sorry.”</p>
<p class="indent">“You owe me an interview.”</p>
<p class="indent">“Steve, I can’t.”</p>
<p class="indent">“We made a deal.”</p>
<p class="indent">“Terry’s too hot. Stringer and Arledge would cut my
            throat.”</p>
<p class="indent">Chandler didn’t like it, but he knew she was right. The stakes were
            a lot bigger now. “Okay. What have you got for me?”</p>
<p class="indent">“We’re rolling out at seven-thirty.”</p>
<p class="indent">“Who else are you calling?”</p>
<p class="indent">“Nobody.”</p>
<p class="indent">“Thanks,” Chandler said, and punched the button that
            connected him to the NBC Washington Bureau.</p>
<p class="indent">Terry was still drowsy when she told him.</p>
<p class="indent">“We have to move to the house.”</p>
<p class="indent">He looked up, blinking. “Why?”</p>
<p class="indent">“I’m sorry. We have to. Can you read this?” She
            handed him a white card. “Learn it.”</p>
<p class="indent">He forced a smile. “Whatever you say.”</p>
<p class="indent">At 6 A.M. Sally broke the news to General DeVane, the hospital
            administrator.</p>
<p class="indent">“That’s idiotic,” he said.</p>
<p class="indent">“Is the senator in any danger?”</p>
<p class="indent">“No. But he should stay a week for rest and
            observation.”</p>
<p class="indent">“I’m sorry, General. We don’t have a
            week.”</p>
<p class="indent">He straightened. “We take no responsibility.”</p>
<p class="indent">“Thank you, General.”</p>
<p class="indent">Five minutes later, Agent Browning of the Secret Service <a id="page_49"></a>was in the hallway outside Terry’s room, asking to see her. Like all the men
            in the Secret Service, he was tall, muscular, colorless.</p>
<p class="indent">He was also startled. “Seven-thirty?”</p>
<p class="indent">“Yes.”</p>
<p class="indent">“This morning?”</p>
<p class="indent">“Yes.”</p>
<p class="indent">“That’s—” he looked at his watch
            “—an hour and a half from now, ma’am.”</p>
<p class="indent">“Right.”</p>
<p class="indent">“Miss Crain, I have to insist—”</p>
<p class="indent">“Please don’t.”</p>
<p class="indent">He tried to reason with her. “This is a secure environment. The
            senator’s home is not.”</p>
<p class="indent">“Since when is the senator’s home considered a security
            risk?”</p>
<p class="indent">“Miss Crain, the senator has been upgraded to class one protection.
            His home has not been surveyed for—”</p>
<p class="indent">“Upgraded? By whom?”</p>
<p class="indent">“The White House.” He let the words hang there for
            emphasis.</p>
<p class="indent">“Seven-thirty,” Sally said. “On the dot.”</p>
<p class="indent">Browning shook his head. “Yes, ma’am,” he said.</p>
<p class="indent">It was seven twenty-five when Browning returned to give his lecture. Sally
            and Chris stood at Terry’s bedside.</p>
<p class="indent">“Senator, I’m Agent Browning of the Executive Protection
            Division. In class one protection, you will be traveling in a defensive bubble. There
            are advance men and flankers who will precede your party, and a close escort of six
            officers around your person. In case of emergency, these rings will fold in to protect
            you and your party. But we cannot guarantee your safety without your complete
            cooperation. Please follow my instructions without hesitation. You must now regard every
            moment you spend in public as a life-threatening situation.” Then he said,
            “Thank you,” without waiting for an answer, and nodded to the Secret
            Service man at the door.</p>
<p class="indent">“We need a moment for the press,” Sally called after
            him.</p>
<p class="indent">“I’m sorry,” Browning said as he went out. “No
            unscheduled stops.”</p>
<p class="indent">It gave Sally an eerie feeling, traveling that corridor under the control
            of the Secret Service; It was reassuring and disturbing at the same time. The six
            officers surrounding the wheelchair wore bulletproof vests under their jackets, carried
            sidearms, and were never more than arm’s length from Terry. The flankers carried
            stubby Uzi submachine guns for close combat or carbines <a id="page_50"></a>for defense
            against snipers. And they all moved as a unit, timing each part of the trip so that
            doors were opened, elevators waited, hallways cleared just as Terry got there. It was
            very much like a “bubble” as it slid along around them.</p>
<p class="indent">Sally had never encountered anything like it. It gave her a bizarre,
            remote, separated feeling. The, people whose votes might put them into office were
            pushed back, aside, held at a distance. Hospital orderlies and nurses simply shrank
            away. The barrier of Secret Service protection surged down the corridor ahead of her,
            silencing conversation like a cold wave of alienation. This, she suddenly realized, was
            the great paradox of power: that those who have the most power have the most to
            fear.</p>
<p class="indentt">About the only people the Secret Service couldn’t intimidate were
            reporters. Reporters had rights above and beyond those of mere mortals, and they pushed
            those rights to the limit if they were good reporters. So, they were there, even at this
            early hour—reporters, photographers, and news crews—hanging over the
            yellow police barriers in the driveway.</p>
<p class="indent">Agent Browning paused his men inside the door, waiting for a sign from
            outside.</p>
<p class="indent">“Excuse me, Agent,” Sally said softly. “We really do
            need a moment for the press.”</p>
<p class="indent">Browning had enough. “Miss Crain, if you interfere with the Secret
            Service, you are subject to arrest and detention—without bail—until we
            perceive that any danger to Senator Fallon is past.”</p>
<p class="indent">Sally smiled. “You know, I’ve never been arrested on network
            television. Excuse me.” She walked past Browning and out the door.</p>
<p class="indent">The roar of reporters’ questions hit like a sonic boom.</p>
<p class="indent">“Sally, will Fallon accept the nomination?”</p>
<p class="indent">“Has he received death threats?”</p>
<p class="indent">“Will Eastman step aside?”</p>
<p class="indent">Sally smiled and scanned the group until she spotted Andrea Mitchell and
            the camera with the NBC peacock.</p>
<p class="indent">“May I have your attention!” Sally said, and the din
            quieted down. “Senator Fallon will have a brief statement. I’m sorry, no
            questions today. I’m sure you understand.” She turned and waved toward the
            doors, as though cueing Terry and his escort.</p>
<p class="indent">Behind the solar glass, Browning stood watching her.
            “Goddamit,” he muttered. An agent outside rapped on the door <a id="page_51"></a>and nodded. “Move out,” Browning ordered. Two agents
            swung the doors open and Terry’s wheelchair rolled into the sunlight.</p>
<p class="indent">Sun-guns switched on. Strobes fired. The roar of questions mounted. As the
            wheelchair and its entourage hustled toward the open doors of the ambulance, Sally
            stood, seemingly nonchalant but squarely in the path of Browning and his men. The group
            rushed down upon her; Sally stood her ground. For a moment, it seemed that the group
            would run her down. Then, suddenly, Browning lifted his arm. The convoy stopped.</p>
<p class="indent">He glared at Sally. “One minute.”</p>
<p class="indent">“Thanks.” Sally turned Terry’s wheelchair to favor
            the camera from NBC. All the reporters shouted for his attention at once.</p>
<p class="indent">Terry sat, unmoving, under the barrage. He didn’t speak,
            didn’t try to outshout them, didn’t raise his hands for silence. He gave
            no indication that he would speak to them at all. He sat, patiently staring at the
            reporters waving and shoving behind the yellow police barrier. He stared at them in
            silence. Then something odd happened, something Sally had never witnessed before.</p>
<p class="indent">The reporters began to quiet down. One by one, they stopped shouting until
            they had all fallen silent, some shuffling their feet, some readying their notebooks,
            some simply wide-eyed and gaping.</p>
<p class="indent">Sally studied their faces. They were the same hacks, the same stringers,
            the same field crews who had covered the Martinez welcome and had become eyewitnesses to
            murder. But in their faces she saw something that had not been there on the day of the
            assassination: respect, reverence, even a sense of awe.</p>
<p class="indent">She could see that, to them, Terry Fallon was no longer a politician. He
            had become something else—the dream that all reporters live to cover: a genuine
            hero. Sally had been a reporter, and she knew how they thought.</p>
<p class="indent">In 1974, Sally had gone to work at the <em class="italic">Houston
                Post</em>. She had come back from two years in the Peace Corps, hoping to help
            make the Americas a better place. But what she had found in Houston was statutory theft
            on a scale that staggered her imagination. Oil money was pouring into the city like a
            Niagara. The city fathers and the rich-and-powerful decreed that the city would make a
            “great leap forward” to international prominence. That demanded a skyline.
            And for the sake of a skyline, all decency had to give way. Through a cabal of
            condemnations and demises, the barrios of the city were swept away by the great <a id="page_52"></a>backhand of the courts. The lives of the poor were tossed up on the
            rubbish heap with the cinder blocks and tin roofs of their shanties. Sally walked
            through rubble that was worse than anything she had seen in Central America, listened to
            the wailing women, the bitter crying of the children. She wrote the stories that dogged
            her heels and wracked her nights with angry dreams. Then, every morning, she scanned the
            columns of the paper looking for a word, a sign, a signal that someone gave a damn. In
            the end, at the point of rage and hysteria, she confronted her editor with a sheaf of
            unpublished stones. He rolled a yellow pencil between his chubby fingers and heard her
            out.</p>
<p class="indent">“Miz Sally,” her editor said—he always called the
            women on the staff <em class="italic">Miz</em>. “Miz Sally, you just
            don’t get the joke. Buildings come and go. But Houston and the Hobbys go on and
            on.” In that moment, he taught her the bitter lesson that every young idealist
            learns: only those with power have the power to change the world.</p>
<p class="indent">One man seemed willing to speak out for the poor. His name was Terry
            Fallon. He was a history instructor at Rice who was running for the city council in the
            very heart of the barrio. The old politicos snorted and ignored him. The media
            wouldn’t even take him as filler. Even the Hispanics used him for a joke; they
            called him <em class="italic">El Gringo</em>.</p>
<p class="indent">But this Fallon was a tireless campaigner who spent every free minute
            walking the streets and alleys of the barrio, telling anyone who would listen that their
            fight could be won. His Spanish was flawless. His smile was engrossing. And he had a
            vision of a better way. Inevitably, his path crossed Sally’s. Inevitably, they
            saw how much they had in common. Inevitably, he was engaged to be married to someone
            else. But she didn’t care. She adored him and wrote about him anyhow. He was
            elected. Then Sally quit her job and headed north.</p>
<p class="indent">A friend who worked on the <em class="italic">Washington Post</em> got
            Sally a five-minute interview with Ben Bradlee. He took the first three minutes to thumb
            past her tearsheets and scan her sheaf of unpublished tirades. Then he squeaked back in
            his chair and got hold of his suspenders. “I wouldn’t print this
            sentimental crap either,” Bradlee said. He turned back to one of her published
            stories about Terry, tapped his finger on the photograph. “Were you screwing this
            guy?”</p>
<p class="indent">“No.”</p>
<p class="indent">“Don’t screw any politicians while you work for me,”
            Bradlee <a id="page_53"></a>said. “When a Washington politician fucks you, you
            stay fucked. See personnel. Welcome aboard.”</p>
<p class="indent">She went back to Houston, closed her apartment, stuffed her things into
            her car and headed north. On her last day in Texas, the <em class="italic">Houston
                Post</em> carried the story of the fairy-tale wedding of heiress Harriet King to
            dashing Councilman Terry Fallon. Fourteen months later, the Deb-of-the-Year was in an
            institution. In 1978, Terry Fallon came to Washington as Congressman Fallon. That same
            year, Sally quit the <em class="italic">Post</em> to become press liaison for Texas
            Senator Caleb Weatherby. When Weatherby went to jail for bribery, Governor Taylor
            appointed Terry to fill his unexpired term. For Sally, their reunion was the fulfillment
            of a consuming dream. But, then again, she had always believed he would come. He was the
            man the people needed. He was a man to be followed. That is what she saw reflected in
            the wondering eyes of the reporters lining the driveway of Walter Reed Hospital.</p>
<p class="indent">Like Sally Crain, they believed.</p>
<p class="indent">In the silence, Terry spoke. “Today, all Americans mourn the death
            of Octavio Martinez,” he said, his voice clear and fresh as a trumpet call.
            “Colonel Martinez was a patriot and a hero to free people everywhere. I speak now
            to his brothers-in-arms in the jungle. <em class="italic">Estará vengada su
                muerte</em>. His murder will be avenged.” Terry was looking right down the
            barrel of the NBC camera. “To all of you who have expressed so much caring for my
            pain, thank you and God bless you. This wound is nothing,” he said.
            “It’s an honor to carry it.”</p>
<p class="indent">Sally nodded to Browning. Instantly, the Secret Service escort rushed the
            wheelchair toward the ambulance.</p>
<p class="indent">Andrea Mitchell shouted, “What’s next for you,
            Senator?”</p>
<p class="indent">Terry laughed. “I’m going to the convention. Aren’t
            you?” He gave the thumbs up as the Secret Service whisked him aboard and slammed
            the ambulance doors behind him.</p>
<p class="indent">Sally turned to face the bank of cameras. “Senator Fallon will be
            recuperating for a few days. He will attend the Presidential nominating convention as
            cochairman of the Texas delegation. Thank you.”</p>
<p class="indent">Agent Browning grabbed her arm and hustled her toward the last black
            Oldsmobile.</p>
<p class="indent">“Thanks,” she said as they ran. “I owe you. And I
            don’t forget.”</p>
<p class="indent">“Neither do I,” Browning said, and she knew he meant it,
            too.</p>
<a id="page_54"></a>
<p class="indent">The convoy of cars and ambulance rushed off with a squeal of tires and
            blaze of sirens. The reporters dashed to their vans to follow. In a moment, the driveway
            was deserted, except for two men.</p>
<p class="indent">One of them was Mancuso. He watched the ambulance and its escort disappear
            down the street. Then he spit on the pavement. “Hope Fallon skipped the blood
            test.”</p>
<p class="indent">“Jesus, Joe!”</p>
<p class="indent">Mancuso turned toward the door of the hospital. He had a name written on a
            three-by-five card. He handed the card to Ross. “You do it,” he said.</p>
<p class="indentt"><span class="bold"><span class="font1">7:45 A.M.</span></span> It was
            hard to drink coffee, read <em class="italic">The Wall</em>
<em class="italic">Street Journal</em>, watch television, and go to work at the same
            time. But Howard Stringer, the President of CBS News, had the limousine and the knack.
            He had watched the live report on Senator Fallon leaving the hospital with a mixture of
            curiosity and pride. It always gave him a lift when his guys were on the spot where news
            was happening. Then Stringer remembered that he had tuned to channel 4 and was watching
            NBC’s <em class="italic">Today</em>.</p>
<p class="indent">“Shit,” he said, and picked up the car phone and punched
            Recall-6.</p>
<p class="indent">Dave Corvo, producer of the <em class="italic">CBS Morning News</em>,
            answered almost immediately. “Yes, Howard?”</p>
<p class="indent">“Did we have that?”</p>
<p class="indent">Corvo was sitting in the West 57th building, just across from the set of
            the <em class="italic">CBS Morning News</em>. But he had all three networks on the
            monitors before him. “You mean Fallon?”</p>
<p class="indent">“Yes, I mean Fallon.”</p>
<p class="indent">Corvo knew from the sound of his voice that his boss was at the limit of
            his patience. “No. We didn’t have it.”</p>
<p class="indent">“Well, goddamit, why not?”</p>
<p class="indent">“Sally Crain must have tipped Chandler.”</p>
<p class="indent">“Sally who?”</p>
<p class="indent">“The blond. The press girl.”</p>
<p class="indent">“For Christ’s sake, she practically did a stand-up for
            them!”</p>
<p class="indent">“Look, Howard,” Corvo said, and he was beginning to lose his
            composure, too. “Chandler’s got her in his pocket. What do you want from
            me?”</p>
<a id="page_55"></a>
<p class="indent">“Get to her!” Stringer shouted into the phone.
            “Reach her! Am I the only person in this fucking organization who realizes
            that Terry Fallon is going to be Vice President of the United States!”</p>
<p class="indentt"><span class="bold"><span class="font1">7:50 A.M.</span></span> Mancuso
            sat in the corner office of General Green, the hospital administrator, listening to
            Ross’s long string of dull questions. Walter Reed was an army hospital and full
            of uniforms and brass. Mancuso had been in the army. And he hadn’t liked it a
            bit.</p>
<p class="indent">Mancuso’s educational deferment had expired when he graduated from
            law school in May of 1952. A month later, he was drafted. He had just started looking
            for a job. There was an Irish girl who lived on Ocean Parkway—Mary Louise
            Dugan—with whom he’d been keeping company for a year, too. But the Korean
            War was ending in an uneasy truce. Mancuso was called, and he thought he was needed.</p>
<p class="indent">After basic at Fort Bragg, he was transferred to Scofield Barracks on
            Oahu, not far from the site of Pearl Harbor. When he got his first weekend pass, he went
            down to Pearl and took the harbor tour.</p>
<p class="indent">It was one of those bright, breezy tropical days that you see on all the
            travel posters. There was a warm sun, flying clouds. The tour launch stopped near the
            old battleship docks on Ford Island, and the guide led everyone to the port side,
            pointed down into the water. Beneath his feet, lying just below the surface of the bay,
            Mancuso could make out the shape of the battleship <em class="italic">Arizona</em>.
            Tiny bubbles of oil rose from the sunken ship, and a blue slick hazed the surface of the
            sea. Eleven hundred men were entombed there, a few fathoms down, trapped when Japanese
            torpedoes struck the ship at her moorings. The ship’s aerial protruded from the
            water, and the Stars and Stripes flew from its tip, standing out stiff in the westerly
            breeze. The tourists on the launch gawked, goofed, and chattered among themselves. But
            Mancuso pressed his lips together. He found it hard to swallow. Under the water of the
            bay, among the ghostly shadows of the <em class="italic">Arizona</em>, he thought he
            saw what it meant to serve his country. The army had other ideas.</p>
<p class="indent">They assigned him as assistant gardener for a two-star general. <a id="page_56"></a>Mancuso trimmed hedges, pruned bougainvillea, and spread manure. He
            finally made sergeant and transferred to the military police. That was skunk work, too:
            breaking up fights, carrying drunken marines out of whorehouses, raiding dice games. Two
            months before Mancuso’s hitch was up, a recruiter from the FBI came to see
            him.</p>
<p class="indent">The man had a great spiel—engrossing stories of lone agents with
            only their guts and gumption to go on, hunting notorious desperadoes. The 1950s were
            going to be an era of rapid expansion for the Federal Bureau of Investigation, he told
            Mancuso. The FBI was evolving into a sophisticated national police force, our front line
            of defense against domestic communism. McCarthyism was rampant, the House Un-American
            Activities Committee was big news, the Hollywood blacklist was growing. The FBI was a
            great career for a young lawyer who wanted excitement and a chance to serve his
            country.</p>
<p class="indent">It sounded right to Mancuso. He signed up, grabbed his discharge, and went
            to San Francisco. That Irish girl from Ocean Parkway—Mary Louise Dugan—was
            waiting for him in a hotel on the Embarcadero in a white linen shift and nothing else.
            Four days later, they took a bus to Reno and got married. They had their picture taken
            in front of a huge, six-tiered, plaster-of-paris wedding cake. Then they flew east and
            Mancuso reported to the FBI training camp in Quantico, Virginia, for a three-month
            course. He signed up for specialist training in electronics. After the first lab class,
            he realized that meant wire-tapping. Mary Louise found a studio apartment for them. They
            had a mattress on the floor for five months before they could afford a bed. Back then, a
            mattress on the floor was all they’d needed.</p>
<p class="indent">In those days, clandestine microphones were still the size of silver
            dollars and miniaturized tape recorders were science fiction. The only way to pull an
            effective electronic surveillance was to put a tap on every phone line that a suspect
            used. Mancuso was transferred to Los Angeles to set taps for the HUAC surveillance of
            the entertainment business. He spent a lot of time in the basements of buildings on
            Wilshire Boulevard and Canon Drive. He particularly liked tapping the lines of the
            William Morris agents. They had the best jokes—and they had them first. The FBI
            guys used to argue about who got the Morris office and who got Jack Warner. They were
            the only people in town who were always good for a laugh.</p>
<p class="indent">On his first assignment, Mancuso asked to see the authorizing <a id="page_57"></a>warrant. But the special agent in charge brushed him off, said the
            warrant was “on file.” After a while, Mancuso realized there
            weren’t any warrants. The taps were unofficial. They were just a way for Hoover
            to sop up Hollywood’s filth and find a few tidbits he could feed to McCarthy. And
            Hoover kept the investigations unofficial so that, if McCarthy went down, the Bureau
            wasn’t dragged down with him. McCarthy went down in 1954 and the Bureau moved on
            to civil rights.</p>
<p class="indent">Mancuso had nothing against blacks. But he had no affinity for them,
            either. So when all the black power agitation began in the winter of 1955, Mancuso
            couldn’t figure what all the shouting was about. In February of 1956, he was
            transferred to the zone office in Montgomery, Alabama. It was an angry time. The blacks
            said if they couldn’t ride the front of the buses, they wouldn’t ride the
            buses at all. Normally, the white folks didn’t give a whit if the blacks rode or
            walked or stayed home. Except most of the people who rode the buses in Montgomery were
            black, and twenty black nickels made a dollar just like twenty white nickels. And
            without all those millions of black nickels, well, running the buses in Montgomery would
            break the city bank. So, the city fathers were pissed but powerless.</p>
<p class="indent">All this consternation was due to a young black preacher named King. He
            wasn’t doing anything illegal by organizing the bus boycott. But, then again, he
            wasn’t exactly helping to build brotherhood and community spirit in Montgomery,
            either. So, Mr. Hoover decided that the Bureau should get to know Dr. King a little
            better. And the quickest way to do that was to tap his phone. That, Mancuso did for
            seven weeks, listening to King and his wife organize rallies, gossip about their
            neighbors, order pizza, and generally conduct the monotonous life of a happily married
            couple. King was not a Communist. He was not a traitor. He was not a drug addict. He was
            simply a man with a different idea about the way things should be. Mancuso recommended
            the tap be terminated. Instead, the special agent in charge of the Montgomery field
            office called him in for a chat.</p>
<p class="indent">“Joe, you and me, we need to have a come-to-Jesus meeting,”
            the man said. His name was Griffiths and he wore suspenders and a bow tie and yellow
            buckskin shoes. “You not from around <em class="italic">heah</em>, but most
            of the boys kinda like you. Which is unusual for a fella from New York and a <em class="italic">Eye-talian.”</em></p>
<p class="indent">Mancuso sat across the desk in a double-breasted suit with a crisp white
            handkerchief in his pocket showing three points. <a id="page_58"></a>He’d been
            told that Mr. Hoover liked a man who showed clean linen and he never went to
            work-without a nice fresh pocket handkerchief.</p>
<p class="indent">“These <em class="italic">nigras</em> is up to no good and you
            don’t seem to want to find them out,” Griffiths said.</p>
<p class="indent">“King’s no criminal,” Mancuso said.
            “He’s a reverend.”</p>
<p class="indent">“He’s as reverend as my monkey’s uncle,”
            Griffiths said. “And Mr. Hoover’ll decide who’s a criminal and who
            ain’t. How’d you get so sweet on <em class="italic">nigras</em>
            anyhow?”</p>
<p class="indent">“I’m not.”</p>
<p class="indent">Griffiths fingered a stack of typewritten transcripts. “Well, your
            reports sure make ’em sound like you over there keeping company.”</p>
<p class="indent">“If those are no good, why don’t you make up your own and
            send them in?”</p>
<p class="indent">They sent Mancuso west to Nevada, where he could eavesdrop on white people
            and his fluency in Italian might be of some use.</p>
<p class="indent">It was the start of a long series of bad bounces and lateral moves that
            took him everywhere and nowhere, until he sat listening to Dave Ross ask a routine,
            boring set of questions in a doctor-general’s office at Walter Reed Army
            Hospital.</p>
<p class="indent">General Green handed a thick file to Ross. “This is the workup on
            Colonel Martinez.”</p>
<p class="indent">“Including the names of the doctors who conducted the
            tests?”</p>
<p class="indent">“Yes, yes,” Green said. “It’s all
            there.”</p>
<p class="indent">Ross thumbed the file. “The blood tests, for example?”</p>
<p class="indent">“The yellow form. Yes. That one.”</p>
<p class="indent">Ross found it.</p>
<p class="indent">Mancuso watched Green. There was no hesitation in him, no flicker of
            emotion when Ross mentioned the blood tests. If anything, Green was impatient and as
            bored as he was.</p>
<p class="indent">“This … Captain Beckwith,” Ross said. “He drew
            the blood and ran the tests?”</p>
<p class="indent">“Yes.”</p>
<p class="indent">“Would he be in the office today?”</p>
<p class="indent">“No. I told you. Thursday’s his day off. He’s
            probably home.”</p>
<p class="indent">Ross started to page back through his notes, looking for the reference to
            Thursday. But Mancuso reached over and flipped his notebook closed.</p>
<a id="page_59"></a>
<p class="indent">“Thank you, General,” Mancuso said, and stood up to go. Ross
            didn’t understand. But he knew enough to take his cue.</p>
<p class="indentt">Mancuso swung his black Ford across the 14th Street bridge. The address
            in Captain Beckwith’s file was in Arlington—and that bridge to Virginia
            was like a passage to another world.</p>
<p class="indent">Once, the hills west of the Potomac had been a patchwork of forests and
            fields from Leesburg to Mount Vernon. Mancuso used to take a drive there every now and
            then when Mary Louise was alive. Now, government and urban sprawl had gobbled up the
            farms and fields and memories, and the forests around McLean hid the headquarters of the
            CIA. What was left for human beings was street after street of cookie-cutter houses
            stamped across the bulldozed, treeless hills. The human evidence—a bike here, a
            hedge there, a limp basketball hoop hung over a garage—only added to the
            unreality. Mancuso turned his car north on Cadwell Drive. But it could have been Carson
            Drive or Carlisle Drive or Canter Drive. It was all the same now. He looked down, the
            street and shuddered. It was contemporary civilization.</p>
<p class="indent">Ross studied the numbers stenciled on the curb.</p>
<p class="indent">“3121, 3123. There it is,” Ross said. “The white one.
            3125.”</p>
<p class="indent">“They’re all white,” Mancuso said. He drove on
            by.</p>
<p class="indent">“Hey? Where you going?”</p>
<p class="indent">“See the front door?”</p>
<p class="indent">“What about it?”</p>
<p class="indent">“It was open.”</p>
<p class="indent">Ross twisted around in his seat, looked back at the house. The door was
            ajar.</p>
<p class="indent">“Uh-oh,” Ross said.</p>
<p class="indent">Mancuso turned the corner and parked in the next block. They walked up the
            street until they could see the rear of the Beckwith house. Mancuso looked around. The
            street was perfectly still. It was 8:45. Husbands and other workers had left for their
            offices, children for school. Housewives were still hanging around their kitchens over a
            second cup of coffee. There was an eerie stillness. It might have been a ghost town.</p>
<p class="indent">“Come on,” Ross said, and walked up the driveway.</p>
<p class="indent">Mancuso followed him. There was a six-foot cyclone fence between the two
            backyards.</p>
<p class="indent">“What do you think?” Ross said.</p>
<p class="indent">Mancuso looked through the fence at the rear of the Beckwith <a id="page_60"></a>house. The blinds were drawn in the upstairs bedroom windows. An
            air conditioner in one of the windows was humming. The downstairs louvered windows of
            the kitchen were open. There was a girl’s bicycle leaning against the side of the
            garage. A Buick with a blue U.S. Army officer tag was in the driveway.</p>
<p class="indent">“Looks quiet.”</p>
<p class="indent">They stood a moment.</p>
<p class="indent">“Well, we’re here,” Ross finally said.</p>
<p class="indent">“Yeah.”</p>
<p class="indent">Mancuso leaned against a fence post.</p>
<p class="indent">“I’m going over there, Joe,” Ross said.</p>
<p class="indent">“Yeah. I figured.” Mancuso raised his foot. “Gimme a
            lift up.”</p>
<p class="indent">Ross squatted down, locked his fingers, and made a step for Mancuso. When
            the older man caught hold of the top of the fence, Ross straightened up quickly, lifted
            him up and over. Mancuso lost his grip and fell ass-over-teakettle on the other
            side.</p>
<p class="indent">“Jesus Christ!”</p>
<p class="indent">“Are you all right?” Ross couldn’t keep from
            laughing.</p>
<p class="indent">Mancuso got up, brushing himself off. “What are you, a fucking
            comedian?”</p>
<p class="indent">“I’m sorry. I didn’t mean to—”</p>
<p class="indent">“Goddamit,” Mancuso said. He slapped at the stubborn dirt on
            the knees of his trousers. “Come on.”</p>
<p class="indent">Ross pulled himself to the top of the fence, swung a leg over. Then his
            trousers caught on the bare wire at the top. There was a long, loud sound of ripping as
            he jumped to the ground. He landed on all fours.</p>
<p class="indent">“Christ, I almost tore my balls off.” He felt the back of
            his pants. “Aw, shit!”</p>
<p class="indent">“Keep it down.” Mancuso grabbed his arm and turned him
            around. “Let me see.”</p>
<p class="indent">Ross’s trousers were torn up the back from the crotch to the
            belt.</p>
<p class="indent">“They’re ripped, aren’t they?” Ross said.</p>
<p class="indent">“What a fucking clown.”</p>
<p class="indent">“Oh, Jesus, they are ripped,” Ross said, feeling the length
            of the tear. “Shit, I just got these. Forty-seven bucks.”</p>
<p class="indent">“Take them back. Tell them they leak. Come on,” Mancuso
            said. They hunkered down, hurried to the back of the house, and knelt below the kitchen
            window.</p>
<p class="indent">Mancuso stopped to catch his breath. He felt ridiculous. He was a
            middle-aged man with dirty knees and a partner with his <a id="page_61"></a>pants tom
            clean up the back. They were sneaking around the back of a perfectly middle-class house
            in a perfectly middle-class neighborhood like a couple of kids on Halloween, If anyone
            spotted them, he thought the embarrassment would kill him. He was three months from
            retirement. And he knew it was time his years with the Bureau should draw to a
            close.</p>
<p class="indent">“What about it?” Ross said.</p>
<p class="indent">“Lemme take a look.”</p>
<p class="indent">Mancuso raised his head to the windowsill and peeked inside. It was a
            typical boring modern kitchen in a typical boring development house: there were notes
            pinned to the refrigerator with magnetic fruit, a hanging plant in a macramé
            holder, a tall glass jar filled with different kinds of pasta. There was a pot on the
            stove. It was boiling over.</p>
<p class="indent">Mancuso stared at the pot. And as he did, his body went wet and clammy all
            over. He ducked down.</p>
<p class="indent">“Trouble,” he said, and drew his revolver. Ross did the
            same.</p>
<p class="indent">“What is it?”</p>
<p class="indent">Mancuso leaned back against the aluminum siding of the house. He
            didn’t answer. Ross watched him carefully. There were little beads of sweat on
            Mancuso’s forehead.</p>
<p class="indent">“Are you okay?” Ross said.</p>
<p class="indent">“Yeah, yeah,” Mancuso said. “Come on.” He
            pressed his body against the wall of the house, slid forward toward the back door. Ross
            inched along behind him. When they reached the door, Mancuso stopped. He seemed to be
            staring at the knob. Ross didn’t understand; he nudged him. Finally, Mancuso
            reached up and tried the knob. It turned easily in his hand.</p>
<p class="indent">Mancuso sat back down, resting on his haunches.</p>
<p class="indent">“Fuck,” he said softly. “Fuck. Fuck.
            Fuck.”</p>
<p class="indent">“What’s the matter?”</p>
<p class="indent">“Nothing.” Mancuso reached for the doorknob again.
            “Cover.”</p>
<p class="indent">Ross caught his arm. “You cover.”</p>
<p class="indent">Mancuso stopped, looked back at the younger man. “Thought I told
            you never volunteer.”</p>
<p class="indent">“Thought I told you to go fuck yourself,” Ross said and
            smiled.</p>
<p class="indent">Mancuso sat where he was a moment. Then he slid back to let Ross take his
            place at the door. “Keep your head down,” he said. He cocked his revolver
            and turned to the window.</p>
<a id="page_62"></a>
<p class="indent">Ross cocked his revolver. He looked back at Mancuso and took a deep
            breath. Mancuso inched up until his eyes and the barrel of the revolver cleared the
            sill. Then he whispered, “Go.”</p>
<p class="indent">Ross slowly turned the doorknob. The lock clicked free and the door swung
            slowly back, revealing the empty kitchen. Ross got down on his hands and knees and began
            to creep across the threshold. As he did, Mancuso caught sight of his undershorts
            sticking out of his torn trousers.</p>
<p class="indent">“What a fucking sight you are,” Mancuso hissed.</p>
<p class="indent">But Ross didn’t answer. He crept forward across the linoleum floor,
            leaning against the cabinets. He had done this drill a hundred times at the FBI rookie
            camp at Quantico. They had taught him that a startled assailant was more likely to fire
            his first shot at waist level or above, and that the kick of the gun would send his
            second shot higher. They had trained Ross to come in low. They had also issued him a
            Colt .45 automatic. Mancuso made him leave that in his locker, made him buy and carry a
            Smith &amp; Wesson .38 revolver. Automatics jammed, revolvers did not. Ross reached the
            end of the row of cabinets. Around the corner lay the hallway to the front of the house.
            His heart was pounding. He stopped to catch his breath. He could not see Mancuso at the
            windowsill; the cabinets and sink were in the way. But he knew Mancuso was watching the
            hallway. Ross crept to the edge and peeked inside.</p>
<p class="indent">Just around the corner was a young girl, maybe ten years old, wearing a
            pink cotton nightie with a ruffle at the neck. She was lying on the linoleum, staring
            straight into Ross’s eyes. He jumped back. But when he looked again, he could see
            the pool of blood under her cheek was already drying, already turning black. Her mouth
            was open and there were bumps in her forehead, as though something had tried to break
            out from inside her brain. Then he saw that she had been shot twice in the back of the
            head, just below the rubber band that held her blond ponytail.</p>
<p class="indent">Ross felt his stomach chum. He clapped his hand to his mouth to cover the
            sound of his gasping breath. His head spun on a wave of nausea arid a rush of vomit
            burned, up his throat. He gasped and gulped it down. Then something stirred behind the
            swinging door to the dining room. He whirled and leveled his revolver and he screamed at
            the top of his lungs.</p>
<p class="indent"><em class="italic">“Stop where you are!”</em></p>
<p class="indent">The door swung open and a black-and-white cocker spaniel <a id="page_63"></a>rotted into the kitchen. It stopped, looked at Ross. He was drenched in sweat. He
            didn’t know whether to laugh or cry. He lowered his revolver. The dog trotted
            past him to the body of the girl. It sniffed her face. Then it began licking the blood
            from the floor.</p>
<p class="indent">Ross puked.</p>
<p class="indentt">Ross was hanging onto the toilet bowl in the hall bathroom of the
            Beckwith house when Mancuso came downstairs. Mancuso holstered his revolver and started
            picking through the contents of a wallet. He pulled out a U.S. Army ID card. Ross lay
            his cheek against the cold porcelain of the toilet, gulping deep breaths of fresh
            air.</p>
<p class="indent">“Captain Arnold Beckwith,” Mancuso read. “Hema
            … hemato … fuck, who cares?” He stuck the card back in the
            wallet.</p>
<p class="indent">Ross took a handful of toilet paper, wiped his mouth. “Is he
            lead?”</p>
<p class="indent">“Does the Pope wear a beanie? In the den. Guess the dog
            didn’t like him.”</p>
<p class="indent">“Why?”</p>
<p class="indent">“It took a leak on his corpse.”</p>
<p class="indent">Ross puked again.</p>
<p class="indent">“College boys.” Mancuso said, and went out into the hall
            until Ross was done.</p>
<p class="indent">“This isn’t murder,” Mancuso said, when Ross finally
            flushed the bowl and came out of the bathroom. “This is butcher work.”</p>
<p class="indent">The body of a middle-aged woman in a flowered housecoat was spread-eagled
            on the stairs, head down on the second step, begs above her, akimbo. There were streaks
            of blood on the wall behind her and blood soaking a dark patch into the rug where her
            head lay.</p>
<p class="indent">Mancuso had it cased.</p>
<p class="indent">“Shooter came in the front door, went to the den, said
            ‘Hiya, Doc,’ and iced him.” Mancuso pointed to the stairs.
            “Wife heard the noise, came running down. Shooter steps into the archway, pops
            her in the eye from over there. Not a bad shot.”</p>
<p class="indent">Ross followed Mancuso back up the hallway to the kitchen. “Little
            girl tries to run out the back door. Pop-pop. Very neat. Very pro.”</p>
<p class="indent">Mancuso turned off the flame under the boiling pot. Then he sniffed the
            rising steam. “Minestrone. My favorite.”</p>
<a id="page_64"></a>
<p class="indent">Ross leaned against the door, dizzy and sick and angry “Who the
            hell would do a thing like this?”</p>
<p class="indent">Mancuso just stared at him as though he had said the dumb est thing in the
            world.</p>
<p class="indent">Ross was just about to ask him again when the loudspeake from the street
            opened up.</p>
<p class="indent">“You in the house! Attention!”</p>
<p class="indent">Ross and Mancuso went to their knees. They looked at each other.</p>
<p class="indent">The loudspeaker roared again. “This is Captain Brewster o the
            Arlington police. You are surrounded. Throw down you weapons and come out with your
            hands over your head.”</p>
<p class="indent">“Fuck me,” Mancuso said softly.</p>
<p class="indent">Ross shrugged, stood, opened the back door, and began to raise his
            hands.</p>
<p class="indent">Mancuso sighed. “Don’t do that …”</p>
<p class="indent">A burst of automatic rifle fire from the backyard cut through the kitchen
            windows, shattering the Venetian blinds. Ross dove for the floor. Then a cross fire of
            automatic weapons opened up from outside the front of the house. The bullets stitched
            througl the kitchen walls, shattered the glass of the stove, blew the refrigerator open,
            broke the bottles inside, shattered the dishes ii the cabinets above the sink. Mancuso
            and Ross clung to the linoleum, covering their heads as the salvo went on and on and
            glass and plaster showered down upon them.</p>
<p class="indent">“Christ!” Ross shouted over the firing. “What
            the hell is the matter with them?”</p>
<p class="indent">“They’re scared,” Mancuso shouted back.
            “Somebody told them the guys in the house just killed three people.”</p>
<p class="indent">“Who told them that?”</p>
<p class="indent">“The guy who killed the three people, asshole!”</p>
<p class="indent">The roar of the automatic weapons rose to deafening. The kitchen became a
            whirling storm of splintered wood and shattered china.</p>
<p class="indent">Finally, Mancuso rolled over on his back and drew his revolver.</p>
<p class="indent">“What the hell are you doing?” Ross shouted.</p>
<p class="indent">“If it’s okay with you, I’m going to
            surrender.” Mancuso threw his revolver out the empty window frame. “We
            give up!’ he shouted. “Hold your fire!”</p>
<p class="indent">Eventually, the shooting stopped.</p>
<a id="page_65"></a>
<p class="indentt"><span class="bold"><span class="font1">9:05 A.M.</span></span> Ted
            Wyckoff waited in the anteroom outside the Vice President’s office. He had no
            news—and that was bad news. At last, Dan Eastman excused himself from his meeting
            with the NASA planning groups and came out.</p>
<p class="indent">“What have you got?” Eastman said.</p>
<p class="indent">“Nothing.”</p>
<p class="indent">They walked down the hall to Eastman’s private study.</p>
<p class="indent">“Did you see the <em class="italic">Today</em>
            show?”</p>
<p class="indent">“No,” Wyckoff said.</p>
<p class="indent">“Moron! They beat you to the punch.”</p>
<p class="indent">They went inside and Eastman slammed the door.</p>
<p class="indent">“I told you no games,” Eastman said, and there was a cold,
            deep-running rage in his voice. “I told you to get into his voting
            record.”</p>
<p class="indent">“We did,” Wyckoff said, and he wasn’t backing down.
            “There’s nothing there.”</p>
<p class="indent">“So, you put the word out that Fallon’s a vegetable?
            Goddamit!”</p>
<p class="indent">“I didn’t do it.”</p>
<p class="indent">“You’re a liar.”</p>
<p class="indent">They stood, staring at each other. Then Wyckoff smiled. “Sure I
            am,” he said. He put his hand on the bar built into the paneled bookcase.
            “Mind if I have a drink?”</p>
<p class="indent">“For Christ’s sake, it’s nine o’clock in the
            morning.”</p>
<p class="indent">“I said, do you mind?”</p>
<p class="indent">“Do what the hell you please,” Eastman said. He went over
            and sat on the couch and slapped his feet up on the coffee table. “Shit,”
            he said.</p>
<p class="indent">Wyckoff measured out a stiff belt of Scotch. “Let me tell you about
            our friend Mister Fallon …” Then he raised his glass in a toast.
            “Success.” But Eastman ignored him. Wyckoff took a drink. Then he propped
            himself on the edge of the desk and crossed his legs.</p>
<p class="indent">“Yesterday, I pulled in all the sources, squeezed every contact,
            called every marker. And you know what I found out about Fallon?”</p>
<p class="indent">“What?”</p>
<p class="indent">“Nothing.” He took a long drink. “Not a goddam
            thing.”</p>
<p class="indent">Eastman sat silent. He watched Wyckoff. The man was grinning like the
            Cheshire cat.</p>
<a id="page_66"></a>
<p class="indent">“Make your point,” Eastman finally said.</p>
<p class="indent">“The guy taught history at Rice. He was a goddam instructor at Rice
            in 1976, for Christ’s sake. Now he’s in the Senate of the United States,
            and if we’re not careful, he’s going to be Vice President and we’ll
            be on our way back to South Philly.”</p>
<p class="indent">“What are you talking about? Make sense.”</p>
<p class="indent">“I’m telling you it’s a fairy tale.”</p>
<p class="indent">“So what?”</p>
<p class="indent">“Fairy tales never happened.”</p>
<p class="indent">Eastman didn’t understand.</p>
<p class="indent">“Listen to me, Dan. I’m telling you there’s nothing
            to find. The guy’s Mr. Clean.”</p>
<p class="indent">“So?”</p>
<p class="indent">“Nobody’s that clean,” Wyckoff said. “Not even
            Santa Claus.” He came over and sat at the other end of the couch.
            “I’ve talked to everybody I can find. I was on the phone to Texas half the
            night.”</p>
<p class="indent">“And what did you do the other half?”</p>
<p class="indent">Wyckoff started to answer, but Eastman abruptly held up his hand.
            “Forget I asked.”</p>
<p class="indent">“You know who put him into Congress?” Wyckoff said.</p>
<p class="indent">“Who?”</p>
<p class="indent">“Nobody. His congressional district in Houston is a barrio.
            There’s no special interest money even if Fallon wanted it.”</p>
<p class="indent">“So, the guy campaigned. He worked. He won a seat
            fair-and-square.”</p>
<p class="indent">“What about Governor Taylor?” Wyckoff said. “Why did
            he pick Fallon to serve out Weatherby’s term?”</p>
<p class="indent">“Everybody knows why. Taylor was looking for a white
            knight.”</p>
<p class="indent">Wyckoff began to laugh.</p>
<p class="indent">“What’s so funny?” Eastman said.</p>
<p class="indent">But Wyckoff couldn’t stop laughing. Then Eastman reached over and
            grabbed his lapel.</p>
<p class="indent">“I said what’s so goddam funny?”</p>
<p class="indent">“You are,” Wyckoff said. “You really believe in the
            American dream.”</p>
<p class="indent">Eastman threw his lapel back at him. Then he got up and went to the door
            and yanked it open.</p>
<p class="indent">“Get out,” he said.</p>
<p class="indent">But Wyckoff didn’t move.</p>
<p class="indent">“I said, get out.”</p>
<a id="page_67"></a>
<p class="indent">“It’s pussy,” Wyckoff said softly.</p>
<p class="indent">“What?” Eastman said. Then he shut the door.</p>
<p class="indent">“Fallon’s wife’s in a looney bin. She’s been
            there since 1977.”</p>
<p class="indent">“And?”</p>
<p class="indent">“Who’s he been screwing in the meanwhile?”</p>
<p class="indent">“Who?”</p>
<p class="indent">“Nobody.”</p>
<p class="indent">“Nobody?” Eastman said, and he was beginning to get the
            drift.</p>
<p class="indent">“Nobody.” Wyckoff took another sip of Scotch.</p>
<p class="indent">“I don’t believe it.”</p>
<p class="indent">“I don’t believe any of it.”</p>
<p class="indent">“What about that blond?” Eastman said. “The press
            girl who works for him?”</p>
<p class="indent">“Sally Crain? No.”</p>
<p class="indent">“You’re sure?”</p>
<p class="indent">“I got it straight from that creepy little fuck, Van Allen. Anyhow,
            it’s too obvious.” Wyckoff stood up and went back to the bar. “No.
            With this guy it’s going to be something real dark and real secret and real, real
            ugly.” He put his empty glass down. “Now, if you have no objection, I
            think I’ll find out what it is.”</p>
<p class="indent">He waited. Dan Eastman stood where he was a moment. Then he opened the
            door and went back down the hall to his meeting.</p>
<p class="indent">“Thanks for the drink,” Wyckoff said to the door closing
            behind him.</p>
<p class="indentt"><span class="bold"><span class="font1">9:35 A.M.</span></span> Mancuso
            had to say one thing for Arlington, Virginia. They had a nice jail, and the chow
            wasn’t bad. It was a better breakfast than he usually got in the Bureau
            Cafeteria. Ross didn’t feel like eating, so Mancuso ate his, too.</p>
<p class="indent">“How the hell can you eat?” Ross said in disgust.
            “Jesus, it was a slaughterhouse.”</p>
<p class="indent">“Yeah, right,” Mancuso said, his mouth full.</p>
<p class="indent">“Fuck.” Ross hung on the bars of the cell door. “When
            are we getting out of here?”</p>
<p class="indent">“What’s your hurry?”</p>
<a id="page_68"></a>
<p class="indent">Ross turned on him. “You really don’t care if we break this
            thing, do you?”</p>
<p class="indent">Mancuso went on eating.</p>
<p class="indent">“You didn’t want to go in that house,” Ross said.</p>
<p class="indent">“Give it a rest.”</p>
<p class="indent">“When you saw the back door was open, you sat down on your chops
            and didn’t want to go inside.”</p>
<p class="indent">Mancuso looked up. “Come on,” he said wearily, and bit into
            another sausage.</p>
<p class="indent">“Hey, Joe. I got eyes. I saw it.”</p>
<p class="indent">Mancuso went on chewing until he had finished the sausage. Then he wiped
            his mouth. “So?”</p>
<p class="indent">“How come?”</p>
<p class="indent">“What do you care?”</p>
<p class="indent">“I want to know.”</p>
<p class="indent">Mancuso sat a long moment on the edge of his bunk. In his dirty pants and
            open-collar shirt, he looked old to Ross, older than Ross had ever known him. The
            jailers had taken away their belts and shoelaces and their neckties. Sitting there with
            the tongues of his shoes hanging out, Mancuso seemed a shadow, a memory, an old man with
            no place to go. “Maybe I was scared,” Mancuso said.</p>
<p class="indent">“Scared of what?”</p>
<p class="indent">Mancuso shrugged, looked down at the tin plate on his knees. “Maybe
            I’m not up to it anymore.”</p>
<p class="indent">Ross looked down at the older man and there was much kindness and
            compassion in his face. Then he said, “You know, Joe, you’re full of
            shit.”</p>
<p class="indent">Mancuso went back to his breakfast.</p>
<p class="indent">“You weren’t scared when the Arlington police opened
            up,” Ross said. “I was looking right at you. They could have made chopped
            liver out of us. You weren’t scared at all.”</p>
<p class="indent">Mancuso waved it away with his fork. “Those locals can’t hit
            a cow in the ass with a bucket.”</p>
<p class="indent">Ross said softly, “Cut the shit, Joe.”</p>
<p class="indent">But Mancuso wouldn’t answer.</p>
<p class="indent">Ross sat down on the opposite bunk. “Somebody got to that general
            at Walter Reed before us, didn’t they?” he said quietly. “Somebody
            else got Doctor Beckwith’s name and address.”</p>
<p class="indent">Mancuso shrugged. “Maybe.”</p>
<p class="indent">“Somebody’s trying to cover this up—and
            they’re half a jump ahead of us.”</p>
<a id="page_69"></a>
<p class="indent">“Maybe.”</p>
<p class="indent">“Who is it, Joe?”</p>
<p class="indent">Mancuso shook his head.</p>
<p class="indent">“I think you know,” Ross said, and he was starting to lose
            his temper.</p>
<p class="indent">“I don’t know,” Mancuso said. “And if
            you’ve got any sense, you don’t want to know either.”</p>
<p class="indent">Ross stood up. He was angry. “Well, if you thought somebody was
            laying for us in that fucking house, why didn’t you call for some goddam
            backup?”</p>
<p class="indent">And in the next moment he realized that he had answered his own
            question.</p>
<p class="indent">They stayed like that a while, Mancuso looking at the tin plate, Ross
            staring down the hallway. Then Ross said, “Jesus Christ …”</p>
<p class="indent">“I don’t know,” Mancuso said. “But we gotta
            have a plan.”</p>
<p class="indent">“What plan?”</p>
<p class="indent">The heavy steel door at the end of. the corridor slid open. Supervisory
            Special Agent Barney Scott of the FBI was standing behind it, hands on his hips. He
            wasn’t happy. The police lieutenant next to him said, “That
            them?”</p>
<p class="indent">Scott scratched his balls.</p>
<p class="indent">“Okay. Turn ’em out,” the lieutenant said.</p>
<p class="indent">Somewhere out of sight, a guard flipped a switch and a huge groaning of
            metal sounded as the bolts on the cell began to move. Ross handed Mancuso his
            jacket.</p>
<p class="indent">“I was scared, too,” Ross said. But the noise of metal on
            metal was so loud that Mancuso didn’t hear him.</p>
<p class="indentt"><span class="bold"><span class="font1">9:45 A.M.</span></span> By the
            time the Secret Service had swept Terry’s home in Cambridge, Maryland, for
            internal bugs, microwave transmission, explosives, clandestine microphones and cameras,
            sniper angles and salients, they had compiled a list of dos and don’ts that would
            have satisfied a Jewish mother.</p>
<p class="indent">Don’t answer the phone or front door; the Secret Service will do
            both. Don’t sit in the front parlor until the glass is replaced with bulletproof.
            All mail, gifts, foodstuff, and parcels that enter the house must go through an X-ray
            and an electronic sniffer to <a id="page_70"></a>detect explosives. The two front
            upstairs bedrooms are off-limits (too vulnerable to snipers). The rear patio and
            swimming pool, too (until the adjoining neighbors can be vetted and the block
            secured).</p>
<p class="indent">Terry stretched wearily on the curving leather sofa in his robe and
            pajamas, listening to the director of the security survey unit. Sally listened,
            too—and as she did, a new life took shape before her.</p>
<p class="indent">There would be men in the house from now on; armed men who took turns
            watching the children on bicycles and the milkman and mailman on Crescent Drive. Men in
            dark, nondescript suits would begin knocking on the doors of unsuspecting people who
            were family and friends of Katrina, the tubby Danish housekeeper, and Jenny, the cook,
            asking a lot of questions about their politics and their habits. Someone would stop by
            to visit Roy, the gardener, and ask a long list of questions about the men who cut the
            lawn and pruned the fruit trees. And Matsuda, the pool man whose Nisei parents were
            interned in 1942, would get even more of the same.</p>
<p class="indent">Everyone who came to the house, whether lifelong friend or party associate
            or visiting dignitary or member of the clergy—all would have to present
            credentials and be identified, all would pass through an airport-style metal detector,
            all would be videotaped coming and going, their automobile license plates noted and
            checked.</p>
<p class="indent">Sally listened to the litany of rules and regulations with a growing
            feeling of sadness and alienation. It was another bubble of protection, but one that
            would constantly lie upon their shoulders like a smothering blanket. It was an end to
            accessibility, it was an end to privacy. It was the start of a life in retreat.</p>
<p class="indent">When he was finished, the director put a copy of the typewritten list and
            his card on an end table. Then he said, “Thank you, Senator,” and left.
            Agent Browning followed. When they had gone, Terry put his head back on the pillow and
            sighed, let his eyes roll shut.</p>
<p class="indent">Sally waited. She looked at Chris. Chris shrugged. They watched Terry
            together until they were sure he was asleep. Then Sally nodded to Chris. They rose and
            began to tiptoe toward the door.</p>
<p class="indent">“Are you going?” Terry’s voice said behind them.</p>
<p class="indent">Sally turned. “We thought you were—”</p>
<p class="indent">“No, no,” he said, and there was a little weak smile in the
                <a id="page_71"></a>corners of his mouth. “It’s all the tension. The
            security. The men-at-arms. There’s never been a gun in this house before.
            What’s happening to this country?”</p>
<p class="indent">Chris took a long breath. Then he stared at the floor without trying to
            answer.</p>
<p class="indent">But Sally went to Terry, knelt beside the couch, took his hand in hers.
            “We have to change it. We have to make it better.”</p>
<p class="indent">“I don’t know,” Terry said. “I don’t
            know anymore.”</p>
<p class="indent">“We can if we believe we can. We can if we start today.”</p>
<p class="indent">Terry smiled. He touched her arm. “Not today. Today I’m
            going to sleep until tomorrow. You do the same.”</p>
<p class="indent">“There’s so much to do.”</p>
<p class="indent">“No,” he said. “We all need a rest. Take the day off.
            Come back tomorrow.”</p>
<p class="indent">“I’ll go downtown and get my things. I’ll stay in the
            guest house until we go to the convention,” she said. “Chris will take
            me.”</p>
<p class="indent">“Sure,” Chris said from the door. “In my bulletproof
            Toyota.”</p>
<p class="indent">Terry smiled at her and lay his hand against the side of her face. She
            snuggled into it, into the warm hollow of his palm. And she heard him say, half aloud,
            to no one and to everyone, “What’s the matter with this country? Have we
            all gone mad?”</p>
<p class="indentt"><span class="bold"><span class="font1">10:10 A.M.</span></span>
            “…Couple of fucking dopes … whole fucking house shot to shit
            … be filling out those fucking goddam reports until Christmas, for
            Christ’s sake.” Barney Scott muttered as he paged through the Martinez
            autopsy report. Mancuso and Ross, in a pair of cotton work pants, stood in the center of
            his office, shifting from foot to foot, uneasy.</p>
<p class="indent">“I don’t see nothing about AIDS,” Scott said, and
            slapped the file closed.</p>
<p class="indent">“It’s in there,” Ross said. “I read
            it.”</p>
<p class="indent">Scott threw the file across his desk. “Show me.”</p>
<p class="indent">Ross thumbed through the papers and found the yellow form. Mancuso plunked
            himself down in the corner looking very bored. Scott stared hard at Mancuso.</p>
<p class="indent">“Shot up the fucking house like a goddam fucking cheese,”
            Scott said.</p>
<a id="page_72"></a>
<p class="indent">“Wasn’t my idea.”</p>
<p class="indent">“Did I ever tell you you’re a shmuck?”</p>
<p class="indent">“Yeah,” Mancuso said. “You’re real free with
            the compliments.”</p>
<p class="indent">Ross looked up. “I don’t get it. It was right
            in—”</p>
<p class="indent">“In your fucking imagination,” Scott said.</p>
<p class="indent">Mancuso sat, picking his ear. “Call the doc,” he said.
            “He’ll tell you.”</p>
<p class="indent">“Which doc?”</p>
<p class="indent">“What’s-his-name. Summers. Forensics. The
            four-eyes.”</p>
<p class="indent">“Yeah. I’ll do that,” Scott said and made a note to
            himself. “I’ll do that.”</p>
<p class="indent">Ross glared at Mancuso.</p>
<p class="indent">“Although …” Mancuso scratched his chin.
            “Maybe he was talking about hearing aids.”</p>
<p class="indent">That got Scott’s attention. “Hearing aids?”</p>
<p class="indent">“Yeah,” Mancuso said. “You get it from listening to
            assholes.”</p>
<p class="indent">Scott sat back, really angry. “Get the fuck out of
            here!”</p>
<p class="indent">They did.</p>
<p class="indent">Ross caught up with Mancuso in the corridor.</p>
<p class="indent">“What did you do that for?”</p>
<p class="indent">“What?” Mancuso kept walking.</p>
<p class="indent">“Set up Doc Summers. Goddam it, Joe, you just fed him to
            Scott.”</p>
<p class="indent">“Gimme a break.”</p>
<p class="indent">“Don’t fuck with me, Joe.” Ross was really angry.
            “You did it. I heard you.”</p>
<p class="indent">They stopped at the elevator. Mancuso pushed the button.</p>
<p class="indent">He said softly, so softly Ross could barely make out the words, “We
            got to find out if Scott’s on our side.”</p>
<p class="indent">But Ross was hot. “What happens to Summers if he
            isn’t?”</p>
<p class="indent">“Shut up, Dave,” Mancuso said, and looked up and down the
            hall.</p>
<p class="indent">“What happens to him?”</p>
<p class="indent">Mancuso shrugged. “Life’s a bitch.”</p>
<p class="indent">Ross looked like he wanted to spit. “For Christ’s sake,
            Joe.”</p>
<p class="indent">The elevator doors opened. They stepped inside. Mancuso pressed the button
            for the lobby.</p>
<p class="indent">Ross said, “Now, listen to me, Joe—”</p>
<p class="indent">But Mancuso smiled and took hold of his arm, his grip like a vise. Ross
            broke off in midsentence. His eyes followed Mancuso’s <a id="page_73"></a>to the
            front of the car. Above the control panel was a small black speaker grating. Above that,
            the lens of a television camera.</p>
<p class="indent">“Don’t you think you’re overdoing it?” Ross
            said when they were out on E Street.</p>
<p class="indent">“You listen to me,” Mancuso said. “In 1954, the
            Bureau had me catching Commies for McCarthy. Now they say McCarthy’s a dirty
            word. In 1956, they sent me to bug some black preacher in Alabama. Now his
            birthday’s a national holiday. In 1971, I went to Canada after conscientious
            objectors. Then Carter gave them amnesty and a party at the Kennedy Center. Every time
            the Bureau waves the flag, you can bet they’ll hand you the shitty end of the
            pole.”</p>
<p class="indent">They stopped at the corner.</p>
<p class="indent">“You don’t believe in anything, do you?” Ross
            said.</p>
<p class="indent">“Lots of things. But not people.”</p>
<p class="indent">Ross shook his head.</p>
<p class="indent">“No heroes, kid,” Mancuso said. “Only
            sandwiches.”</p>
<p class="indent">They walked their separate ways.</p>
<p class="indentt"><span class="bold"><span class="font1">11:50 A.M.</span></span> Chris had
            driven Sally back to Georgetown to pack. And she had almost finished when she went into
            the bathroom and realized that she was out of everything. So, she pulled a sweater over
            her yellow sundress and ran down the cobbled hill to the Peoples Drug on Wisconsin
            Avenue.</p>
<p class="indent">She grabbed a cart and rattled up and down the aisles, picking up
            hairspray and hand lotion, plus a big bottle of Scope that was on sale. And she was so
            busy looking for toothpaste that she ran smack into someone else’s cart at the
            end of the aisle.</p>
<p class="indent">“I’m sorry,” she said. “I wasn’t
            watching.”</p>
<p class="indent">The young man was rubbing his knee. “You really ought
            to—” Then he looked up at her. “Sally?”</p>
<p class="indent">She blinked and stared at him.</p>
<p class="indent">He stood up. He was tall, over six feet, fair-haired and blue-eyed, and
            dressed in a crisp blue blazer and button-down shirt, gray slacks, and loafers. He was
            gorgeous.</p>
<p class="indent">“Sally Crain?” he said.</p>
<p class="indent">“Yes?”</p>
<p class="indent">“Hey, it’s me. Steve Thomas.”</p>
<p class="indent">But she couldn’t place him.</p>
<a id="page_74"></a>
<p class="indent">“Steve Thomas. You know. Emory University. Dooley’s Frolics,
            1968.”</p>
<p class="indent">She smiled the best she could. “I don’t—”</p>
<p class="indent">“The Deke House.”</p>
<p class="indent">She laughed and opened her hands. “I must be getting senile. I just
            can’t—”</p>
<p class="indent">“Your girlfriend Angie was dating Jerry Kamer.”</p>
<p class="indent">“God,” she said, and laughed out loud. “I
            haven’t thought of Angie and Jerry for—”</p>
<p class="indent">“Got time for lunch?”</p>
<p class="indent">“Gee, no, I’m sorry,” she said. “I just ran
            out to grab a few things and—”</p>
<p class="indent">“Gotta eat sometime.”</p>
<p class="indent">He really was gorgeous.</p>
<p class="indent">“A quick one,” she said.</p>
<p class="indentt"><span class="bold"><span class="font1">Noon</span></span> “Two
            with everything,” Mancuso said, and handed a five-dollar bill to the pushcart
            vendor. He didn’t like driving all the way back to Arlington in the midday
            traffic, but it was halfway to McLean and there was always a crowd around the Iwo Jima
            Monument in summer. It was a good place to get lost. While he waited for the dogs, he
            watched the families taking Polaroids in front of the bronze statue of the flag-raising
            on Mount Suribachi. Some of the families were Japanese tourists. Mancuso stared. Fucking
            Japs taking pictures in front of a memorial to a battle where they got their ass kicked.
            Funny how time turned things upside down.</p>
<p class="indent">“Easy on the kraut, Mac,” Harry Wilson said, behind him. He
            reached past Mancuso and took his hot dog.</p>
<p class="indent">Mancuso got his change, followed Wilson to a bench and sat down beside
            him.</p>
<p class="indent">“Got to ease up on spices,” Wilson said. “Burns me at
            both ends.”</p>
<p class="indent">“Ulcers?”</p>
<p class="indent">“Bullshit,” Wilson said. “I’m so fed up with
            bureaucratic bullshit, I smell like a cow when I fart.”</p>
<p class="indent">Mancuso had known Wilson a long time, and he knew what he meant. Wilson
            had joined the CIA in 1961, in time to help kick the Vietnam mess into high gear. He was
            infiltrating peace <a id="page_75"></a>groups in Chicago in 1968 when he met Mancuso,
            who was doing the same thing for the FBI. They used to get together on weekends and go
            to the south side and eat deep-pan pizza and drink beer. After the Democratic convention
            riots, Mancuso was sent off to chase draft evaders and Wilson was promoted to deputy
            station chief in Teheran because the shah wanted an expert on infiltrating radical
            groups. Wilson was on leave in Paris in 1978 when some of the “students”
            he was watching overran the embassy and began the seige that hamstrung Jimmy Carter. So,
            Wilson came back to Langley as a member of “the intelligence
            community”—in other words, he was a spook who made no bones about it. Now,
            he was too old and too well recognized for field work. He hadn’t made deputy
            director or even section chief, so he wound up a recruiter. There were a lot of guys
            like Wilson in the CIA, older guys who couldn’t be pushed out and had to be
            treated with a certain respect. They knew too much. It didn’t pay to let them
            become disgruntled.</p>
<p class="indent">“Why don’t you get out?” Mancuso said.</p>
<p class="indent">“I’m sixty-two in February, Joe. Then I’m a memory.
            If I cut out early, they may think I’m going to free-lance. The company
            doesn’t like guys who go into business for themselves.”</p>
<p class="indent">“Like Rolf Petersen,” Mancuso said.</p>
<p class="indent">“Like Rolf Petersen.”</p>
<p class="indent">They sat a while, eating. Mancuso watched Wilson. He was a spiffy dresser.
            Nice brown suit with a nice neat crease in his trousers and a nice shine on his shoes.
            He wore a silk necktie and a white-on-white shirt. And he always wore steel-rimmed
            sunglasses. Mancuso could see why Wilson hadn’t made it to the top. He looked
            like a fucking spy.</p>
<p class="indent">“I got trouble, Harry,” Mancuso said.</p>
<p class="indent">“How come?”</p>
<p class="indent">“I got to find Petersen.”</p>
<p class="indent">Wilson wiped his mouth with his napkin, wrapped the end of the hot dog in
            it, and dropped it in the wastebasket beside the bench. “You got trouble,”
            he said.</p>
<p class="indent">“You know where to look?”</p>
<p class="indent">“No. And I’m not curious.” Wilson sat back in the
            sun, slouched down, and adjusted his glasses on his nose as though he were going to take
            a nap.</p>
<p class="indent">“Can you get me a picture and service record?” Mancuso
            said.</p>
<p class="indent">Wilson leaned his head back, closed his eyes. “This an official
            request?”</p>
<a id="page_76"></a>
<p class="indent">“No.”</p>
<p class="indent">“I’ll try.”</p>
<p class="indent">Mancuso chewed the last bite of his hot dog.</p>
<p class="indent">“Where you gonna go after you retire, Harry?”</p>
<p class="indent">“Me and the wife got a little place in Vero Beach,” Wilson
            said. “It’s a motel. Twenty units, room for twenty more. What about
            you?”</p>
<p class="indent">“I don’t know. Got no plans.”</p>
<p class="indent">“Shame that you lost Mary Louise.”</p>
<p class="indent">“Yeah.” Mancuso sat a while, thinking about that. Then he
            stood up.</p>
<p class="indent">Wilson’s eyes opened. “Hey, Joe …”</p>
<p class="indent">“Yeah?”</p>
<p class="indent">“Petersen’s a real mean fuck. You’re gonna have to
            kill him. You know what I mean?”</p>
<p class="indent">“Yeah,” Mancuso said, and his voice was old and tired.
            “I seen his work.”</p>
<p class="indent">Slowly, he walked away.</p>
<p class="indentt"><span class="bold"><span class="font1">12:25 P.M.</span></span>
            “To a quick lunch,” Steve Thomas said and held up his glass of
            burgundy.</p>
<p class="indent">“You’re a devil,” Sally said. “I’ll die
            if anyone I know comes in and sees me like this.” She held the front of her
            sweater closed over her sundress and touched her glass to his. They drank.</p>
<p class="indent">“Don’t be silly. You’re beautiful.”</p>
<p class="indent">“Oh, sure. No makeup. Big, dark rings under my eyes. Dressed like a
            teenager. I don’t even have stockings on.”</p>
<p class="indent">In fact, the maitre d’ had raised his eyebrows when they walked
            into Clyde’s. But Steve gave him ten bucks, and he found a corner booth in the
            back. Now, Sally hunched down in the booth, feeling silly and out of place amid the
            trendy Georgetown lunchtime crowd.</p>
<p class="indent">“I’m in real estate,” Steve said. “In Golden,
            Colorado.”</p>
<p class="indent">“Anywhere near the university?”</p>
<p class="indent">“Er … yeah. What about you? What about the girl I met at
            Dooley’s Frolics a thousand years ago?”</p>
<p class="indent">Sally tasted the burgundy. It was sweet and dark and heavy with the smell
            of fruit and France. “Oh, I don’t know. She <a id="page_77"></a>wandered
            around for a long time before she found herself. The Peace Corps, a couple of weekly
            papers, and the <em class="italic">Houston Post</em>. Then up here to the <em class="italic">Washington Post</em> and politics.”</p>
<p class="indent">“I’m impressed,” he said, and she could see he
            was.</p>
<p class="indent">“I was press aide for Senator Weatherby ’til … you
            know.”</p>
<p class="indent">“Weatherby, Weatherby? Wasn’t he the one
            that—”</p>
<p class="indent">“Yes,” Sally said.</p>
<p class="indent">“Hey, that must have been something to live through.”</p>
<p class="indent">“Like the song says, ‘I’ve seen fire and I’ve
            seen rain.’”</p>
<p class="indent">“I guess,” Steve said. There was tenderness in his eyes, and
            caring. She liked him.</p>
<p class="indent">“Now I work for Senator Fallon.”</p>
<p class="indent">Steve had a mouthful of wine. He coughed and almost choked.
            “Jesus,” he said, wiping his mouth. “You mean the guy
            who—”</p>
<p class="indent">“Yes.”</p>
<p class="indent">“My God! I saw the whole thing on television!”
            Then he stopped himself. “God—that was you! Holding his
            arm.”</p>
<p class="indent">“Yes.”</p>
<p class="indent">“It must have been terrifying.”</p>
<p class="indent">“Yes.”</p>
<p class="indent">“Are you all right? I mean, is he all right? Is
            he—”</p>
<p class="indent">“He’s recovering. We’re, working out of his house
            while he recuperates. That is”—she looked at her
            wristwatch—“if I can get my shopping done today.”</p>
<p class="indent">“Gee, I’m sorry. I didn’t understand. Let me get a
            waiter.” He twisted around in his chair.</p>
<p class="indent">She looked down at his left hand on the tablecloth. It was a strong hand,
            tanned, with neat, rounded fingernails. There was no wedding ring, and no suntan line
            where a ring might have been removed. It looked like a gentle hand. It was the kind of
            hand she needed. Maybe she could afford just one day off.</p>
<p class="indentt"><span class="bold"><span class="font1">5:35 P.M.</span></span> Joe
            Mancuso sat at the end of the bar at Gertie’s, at the corner of 11th and F
            streets. He sat there for an hour or so after work every weekday. Some people said there
            were only three days in the last thirty years that you couldn’t find Mancuso on
            that bar stool, at six o’clock: the day Jack Kennedy was shot, the day after the
            Redskins won the Superbowl, and the <a id="page_78"></a>day after Mary Louise died. If
            Mancuso was sober enough to stand, you’d find him sitting in that chair.</p>
<p class="indent">Gertie put a double of Jack Daniel’s on the bar in front of
            him.</p>
<p class="indent">“Whatcha workin’ on now, Joey. Somethin’
            big?”</p>
<p class="indent">“Yeah.” Mancuso took a belt. It felt good going down.</p>
<p class="indent">Gertie leaned forward, put her elbows and her tits on the bar. “No
            kiddin’?” she said. “Lemme hear.”</p>
<p class="indent">“Well …” Mancuso looked up and down the bar. The
            regulars were there: a few old FBI guys, some of the lifers from the Labor Department.
            There were three or four hookers, including his friend, Mandy, working the weekday
            evening trade. He leaned in toward Gertie. “Somebody’s been stealing,
            quarters out of the prophylactic vending machine in the President’s
            office.”</p>
<p class="indent">“No! You don’t mean it?”</p>
<p class="indent">“Yeah,” Mancuso said gravely.</p>
<p class="indent">“The President’s own office,” she said, and her voice
            was full of wonder.</p>
<p class="indent">“Shhhhh.”</p>
<p class="indent">Gertie shook her head. “Some people.” She was still shaking
            her head when she went down the bar to set up another drink.</p>
<p class="indent">Ross slid onto the stool beside Mancuso.</p>
<p class="indent">“Whatcha find out?” Mancuso said.</p>
<p class="indent">“Gertie, gimme a gin and tonic,” Ross called. “No.
            Better make it a gimlet.”</p>
<p class="indent">“When are you gonna stop drinking that pussy stuff?”</p>
<p class="indent">“Fuck you.”</p>
<p class="indent">“Bad news, I gather.”</p>
<p class="indent">“Shit.” Ross turned on his stool. “Doc Summers
            doesn’t remember anything about AIDS.”</p>
<p class="indent">Mancuso snorted. “How about that?”</p>
<p class="indent">“Yeah. How about that?”</p>
<p class="indent">Gertie put Ross’s drink down on the bar. He gulped it
            gratefully.</p>
<p class="indent">Mancuso said, “And when you called he had a lot of appointments and
            not much time to talk.”</p>
<p class="indent">“Something like that.”</p>
<p class="indent">“Which means Scott ain’t on the side of the
            angels.”</p>
<p class="indent">“Who do we see about that?” Ross said.</p>
<p class="indent">“Nobody.”</p>
<p class="indent">“Yeah.”</p>
<p class="indent">They sat a while, staring at the row of bottles on the back bar.</p>
<a id="page_79"></a>
<p class="indent">Then Ross said, “Are they gonna kill us?”</p>
<p class="indent">“Only if we poke too deep,” Mancuso said. “Three
            months of clerical work. That’s what they want. Then I’ll be on the beach,
            you’ll be transferred, and the trail will be as cold as Kelsey’s
            nuts.”</p>
<p class="indent">Ross took a long swig.</p>
<p class="indent">“Meanwhile, they’re telling us to go the other way,”
            Mancuso said.</p>
<p class="indent">“Which way?”</p>
<p class="indent">“After the guy who shot Martinez.”</p>
<p class="indent">“Who’s telling us?”</p>
<p class="indent">Mancuso stopped and stared at him, just like he had done in the kitchen of
            the Beckwith house, with the body of the murdered little girl at their feet.</p>
<p class="indent">“Is that a serious question?” Mancuso finally said.</p>
<p class="indent">“Yes.”</p>
<p class="indent">Mancuso shook his head. “Yale …” he said.
            “Didn’t they teach you nothing?” He got down off his stool, tossed
            a crumpled five-dollar bill on the bar. “The same people who brought you tax
            loopholes and Selective Service. The rich. The privileged. The powers that
            be.”</p>
<p class="indent">Ross sat, speechless, watching Mancuso walk away.</p>
<p class="indent">At the end of the bar, the hooker, Mandy, reached out and plucked
            Mancuso’s sleeve.</p>
<p class="indent">“How about a date, Joe?”</p>
<p class="indent">“Sorry, kid,” Mancuso said. “I already got
            one.”</p>
<p class="indent">He went out.</p>
<p class="indentt"><span class="bold"><span class="font1">10:10 P.M.</span></span> It had
            been lovely. It had been fun. It had made Sally feel like a girl again. They had lunched
            in Georgetown, then driven down the George Washington Memorial Parkway to Mount Vernon
            in Steve’s convertible with the wind blowing in her hair and the trees heavy with
            green and the sun darting in and out between the branches. On the way back, they had
            stopped and walked along the Potomac. They had dinner by candlelight in an old coaching
            inn on Willow Tree Road.</p>
<p class="indent">It was after ten when Steve brought her back to the door of her townhouse
            in Georgetown. The old-fashioned gas lamps were flickering and the breeze shook a
            pattern of leaves and <a id="page_80"></a>lamplight on her doorstep. She dug in her bag
            for her keys, and he hung over her, keeping his distance, but looking for a kiss.</p>
<p class="indent">“Sure you don’t want me to come in for a nightcap?”
            he said.</p>
<p class="indent">“I do,” Sally said. “But not tonight. I have to
            finish packing.”</p>
<p class="indent">“I’ll only be in Washington five days … and
            nights.”</p>
<p class="indent">“I’ll bear that in mind.” She found her key, slipped
            it into the lock. “Good night, Steve. Thanks,” she said. “It was a
            real nice day. I needed that.”</p>
<p class="indent">She got up on her toes, gave him a peck on the cheek.</p>
<p class="indent">He stood a moment, a little glad and a little disappointed at the same
            time. “I’ll call you,” he said. And he walked back down the
            redbrick steps into the night.</p>
<p class="indent">Sally went inside, locked the door behind herself, and snapped on the
            light.</p>
<p class="indent">There was a gruff-looking man sitting in an armchair in her living room.
            He stood up and went for his pocket.</p>
<p class="indent">Sally dug in her pocketbook for her can of Mace. She seized it, flung her
            purse aside.</p>
<p class="indent">“Stop right there!” she commanded.</p>
<p class="indent">“Mancuso,” he said, and held up his shield and ID card.
            “FBI.”</p>
<p class="indent">She looked at him. “Put it on the table.”</p>
<p class="indent">He did.</p>
<p class="indent">“Back away.”</p>
<p class="indent">She waited until he had moved to the other side of the room, then she
            picked up the folder and looked from the likeness to him. “What’s your
            office address?”</p>
<p class="indent">“Tenth and Pennyslvania.”</p>
<p class="indent">“How many floors in the building?”</p>
<p class="indent">“Seven on the Penn side, eleven on E Street. And three
            basements.”</p>
<p class="indent">She thought a moment. Then she dropped his ID on the table.
            “Okay,” she said, and went and got her purse and put the Mace away.
            “What do you call this?” she said.</p>
<p class="indent">“Last guy I went to talk to about Martinez, somebody killed him and
            his whole family.”</p>
<p class="indent">Sally looked up.</p>
<p class="indent">“Let’s take a ride,” Mancuso said.</p>
<p class="indentt">As they drove down M Street, Sally leaned against her door, watching
            Mancuso in the lights of the oncoming cars. He was a <a id="page_81"></a>stoop-shouldered man in an old brown suit with narrow lapels, frayed cuffs, a
            potbelly and a double chin. He was just the kind of lock-step, civil service dullard
            that she had expected the FBI to assign to the investigation. Just the kind of drudge
            who would never turn up anything that would make an embarrassing headline before the
            nominating convention. She looked at him and realized that the White House had decided
            to bury the investigation along with Martinez. And she wondered if the White House had
            its reasons.</p>
<p class="indent">“Have you any idea who killed Martinez?” she said at
            last.</p>
<p class="indent">“You think Aaron can turn the Braves around this year?”
            Mancuso said.</p>
<p class="indent">“What?” For a moment, she didn’t understand. Then,
            she suspected that she did.</p>
<p class="indentt">Mancuso parked near the Lincoln Memorial. He led the way up the long
            flight of marble steps. It was 10:30, but it was a warm night, and there were tourists
            taking flash photographs, talking in whispers.</p>
<p class="indent">Sally climbed to the top of the stairs behind the slumping figure of
            Mancuso, followed him between the towering columns to the foot of the statue. It glowed
            a warm, granite yellow, and the great head of Lincoln looked down, weary and benign.</p>
<p class="indent">Sally was impatient. “Now, what can I do for you,
            Agent?”</p>
<p class="indent">“How come Fallon loves the greasers so much?”</p>
<p class="indent">Sally blinked and stopped short of an answer.</p>
<p class="indent">“What’s your problem?” Mancuso said.</p>
<p class="indent">“I don’t like your choice of words.” She walked past
            him.</p>
<p class="indent">“Have it your own way.” He followed her.</p>
<p class="indent">“Senator Fallon believes that we can only have peace in this
            hemisphere if the United States supports popular revolutions to—”</p>
<p class="indent">“Okay, okay,” Mancuso said. “I get it.”</p>
<p class="indent">She was growing irritable. “What are your politics, Mr.
            Mancuso?”</p>
<p class="indent">“I haven’t voted since Truman went back to
            Independence.” He took out a pack of cigarettes, offered her one. Brusquely, she
            waved it away.</p>
<p class="indent">As he cupped the flame of the match before his face, he watched her. She
            was a good-looking head, cocky and bossy and full of herself. He knew the type. She was
            probably one of those dames who hung around classy parties until somebody with a <a id="page_82"></a>little power took her for a tumble. Once a woman like that got her
            hooks into a guy, she used his dick as a rung to clamber up the ladder until she got the
            job or the ring or the settlement she wanted. Mancuso knew the type all right—and
            he didn’t like her a bit.</p>
<p class="indent">“All right,” she said. “I haven’t got all
            night. What do you want?”</p>
<p class="indent">“A lead.” He snuffed the match between his fingers, put it
            in his pocket.</p>
<p class="indent">She studied him a moment. He was a rumpled, worn-down nub of a
            man—and a bigot in the bargain. “Suppose I had a lead to give you? What
            then? What do you do? Write a report and sit around for a week until somebody with some
            authority finds the time to read it?”</p>
<p class="indent">“You give me a lead, I follow it up. That’s the way it
            goes.”</p>
<p class="indent">“If you don’t mind, I’d rather talk to the
            agent-in-charge.”</p>
<p class="indent">He flicked an ash to the marble floor. “I’m
            agent-in-charge.”</p>
<p class="indent">“You’re in charge?”</p>
<p class="indent">“Yeah. Look, Sis, what’s your beef?”</p>
<p class="indent">Sally laughed out loud. “The most important political crime of this
            decade and they put <em class="italic">you</em> in charge of finding the
            killer?”</p>
<p class="indent">He was starting to get angry. “What’s so funny?”</p>
<p class="indent">“Forgive me,” she said. “I always knew justice was
            blind. I didn’t know she was stupid.”</p>
<p class="indent">She had a real sharp mouth on her, and Mancuso was getting good and
            pissed. But he knew he’d get a lot more out of her if she thought she had the
            upper hand. “You having a good time?” he said softly.</p>
<p class="indent">“All right,” Sally said. “All right. I give you a
            lead—you run it down. That how it goes?”</p>
<p class="indent">“Yeah. That’s how it goes.”</p>
<p class="indent">It was just what she expected from the Baker administration—a
            passive investigation in the hands of incompetents with no possibility of catching the
            killer or punishing the guilty. It wasn’t a cover-up. Not exactly. Washington had
            learned a lot since Watergate. There was more than one way to suppress evidence; the
            best way was not to uncover it in the first place. It was the kind of thing that Terry
            Fallon would change when he got the chance.</p>
<p class="indent">“I may have a lead for you,” she said. “A man came up
            to me in a drugstore today. A man who claims he knew me in college. He was very
            friendly.”</p>
<a id="page_83"></a>
<p class="indent">“So?”</p>
<p class="indent">“He said he lives in Golden, Colorado. Near the
            university.”</p>
<p class="indent">“So?” Mancuso said.</p>
<p class="indent">“There’s no university in Golden. It’s in
            Boulder.”</p>
<p class="indent">Mancuso put his hands in his pockets. Oh, yeah—she was a real piece
            of work, this one. She could say one thing and mean another while her hands or her eyes
            did something cute to throw you off the track. And she had that open, fresh-as-clover
            beauty that made men want to believe her.</p>
<p class="indent">“What do you think about that?” she said.</p>
<p class="indent">“What do you think about it?”</p>
<p class="indent">“Could be something,” she said. “Could be nothing.
            Just some enterprising reporter trying to get some pillow talk about Senator
            Fallon.”</p>
<p class="indent">“Is there pillow talk about Fallon?”</p>
<p class="indent">“No.”</p>
<p class="indent">“You screwing him?”</p>
<p class="indent">She looked back again, and this time her eyes flashed with anger.
            “Listen, you filthy little—”</p>
<p class="indent">“All right,” Mancuso said, and he knew that he had hit her
            hot button. “So, you’re screwing him. I don’t give a
            shit.”</p>
<p class="indent">She turned on him and she was practically shaking with rage, so much rage
            that she couldn’t speak.</p>
<p class="indent">“All right, all right,” Mancuso said. “You love
            him.” He walked away from her and leaned against a column. All of a sudden, he
            was feeling pretty cocky himself.</p>
<p class="indent">She stepped close to him. “What the hell do you care?”</p>
<p class="indent">“Like I said, honey …” He took a pull on the
            cigarette. “I don’t.”</p>
<p class="indent">They stood a long moment like that, eyeing each other.</p>
<p class="indent">“His name’s Steve Thomas,” Sally finally said.
            “He’s staying at the Four Seasons Hotel.”</p>
<p class="indent">“Come on,” Mancuso said. “Let’s make a phone
            call.”</p>
<p class="indentt"><span class="bold"><span class="font1">11:30 P.M.</span></span> There was
            a <em class="italic">WELCOME NAB</em> sign hung in the lobby of the Four Seasons
            Hotel. The lobby was busy with overfed, middle-aged men in dark business suits with
            badges on their lapels. The badges said “Hi, I’m Fred” or
            “Hi, I’m Dick” and the men were a noisy bunch of backslappers,
            telling each other old blue jokes.</p>
<a id="page_84"></a>
<p class="indent">Mancuso and Sally hung in a corner until Ross came hustling through the
            revolving door, still struggling to knot his tie.</p>
<p class="indent">“You took your time,” Mancuso said.</p>
<p class="indent">“Hey, I was asleep.” Then Ross saw Sally beside Mancuso and
            stopped dead.</p>
<p class="indent">“This is Ross,” Mancuso was saying. “He works with
            me. This is Sally Cain.”</p>
<p class="indent">“Crain,” she said.</p>
<p class="indent">“Yeah, hi,” Ross said, and he couldn’t take his eyes
            off her.</p>
<p class="indent">“Whatever,” Mancuso said. “Now here’s the
            thing—”</p>
<p class="indent">The elevator doors opened and three conventioneers carrying plastic
            highball glasses waltzed off. Behind them, Steve Thomas stepped out and started across
            the crowded, boisterous lobby toward, the cocktail lounge.</p>
<p class="indent">“That’s him,” Sally said. Instinctively, Mancuso and
            Ross turned away.</p>
<p class="indent">“Did he make us?”</p>
<p class="indent">“No.”</p>
<p class="indent">Mancuso looked back over his shoulder. “Which one?”</p>
<p class="indent">“The tall man. With the sandy hair.”</p>
<p class="indent">Mancuso took Sally’s arm. “You just keep him talking fifteen
            minutes. We’ll see what we can find.”</p>
<p class="indent">“Find where?”</p>
<p class="indent">“In his room.”</p>
<p class="indent">“Now, hold on a second.” Sally took a step back. “I
            said I’d call him and point him out. I never said I was going to entertain him
            while you search his room.”</p>
<p class="indent">“You want to find Martinez’s killer or don’t
            you?”</p>
<p class="indent">Sally looked back and forth between the two men.</p>
<p class="indent">“Look, lady,” Mancuso said, and took his hat off.
            “Breaks like this don’t come along every day. You got to give us a
            hand.”</p>
<p class="indent">“But you don’t have a warrant. Don’t you need a
            warrant?”</p>
<p class="indent">“Only if we get caught. Now, please. What do you say?” But
            she didn’t move. She looked at Ross. He shrugged.</p>
<p class="indent">“Okay, okay,” Sally said. “Fifteen minutes. But if he
            lays a hand on me, I’m out of here.”</p>
<p class="indent">“Deal,” Mancuso said.</p>
<p class="indent">She threaded her way across the lobby and disappeared into the cocktail
            lounge.</p>
<p class="indent">“Kinda pushy with her, weren’t you?” Ross said.</p>
<p class="indent">“She’s tougher than she looks. Come on.” They walked
            to the elevator.</p>
<a id="page_85"></a>
<p class="indent">Mancuso said, “You watch, I’ll search.”</p>
<p class="indent">“I’ll search,” Ross said. “I’m
            neater.”</p>
<p class="indent">“Okay.” Mancuso nodded in the direction of the house phones.
            “One ring means move out.”</p>
<p class="indent">Ross got into the elevator. Then he stood, looking at Mancuso.</p>
<p class="indent">“So?” Mancuso said.</p>
<p class="indent">“You gonna tell me the room number?”</p>
<p class="indent">“Oh, yeah, seven-two-four.”</p>
<p class="indent">“See you.” Ross pushed a button and the elevator doors
            closed between them.</p>
<p class="indentt">Sally walked through the busy cocktail lounge. There was an unbroken line
            of conventioneers down the length of the bar, punctuated here and there with
            hundred-dollar hookers. A man on a settee at the far end of the room stood up and waved.
            It was Steve Thomas.</p>
<p class="indent">“Hi, Steve.”</p>
<p class="indent">“Come. Sit. Have a drink.” He looked smart and crisp and
            preppy, not a hair out of place. He smiled and Sally could see the
            anticipation—and lust—in his eyes.</p>
<p class="indent">“I’m glad you called,” he said. He let her sit down;
            then slid in close beside her.</p>
<p class="indent">“I had to run an errand,” Sally said. “Took me right
            past the front door. I thought I’d see if you were still up.” As she
            spoke, she saw Mancuso standing in the entrance to the lounge. He glanced at her, then
            took the last open seat at the farthest end of the bar.</p>
<p class="indent">“My lucky night,” Steve said. He motioned to the waiter.
            “How about a double for openers?”</p>
<p class="indentt">The lock on the hotel room door was a three-channel Schlage, the kind
            with a Teflon insert in the bolt. Ross had straight A’s in locks at
            Quantico; he passed it like it was open. Then he used the knuckle of his index finger to
            switch on the lights in the room and shut the door behind him.</p>
<p class="indent">He put on the rubber surgical gloves and started on the dresser. He pulled
            out a pair of shorts and looked at the label. He got a jolt right off. Steve Thomas
            might be from Colorado, but his underwear was from Woodward and Lothrop.</p>
<p class="indentt">“Must be tough being a beautiful single woman in
            Washington,” Steve Thomas was saying in the cocktail lounge downstairs. <a id="page_86"></a>He leaned toward Sally, so close that they were almost
            touching.</p>
<p class="indent">“Oh, I don’t know.”</p>
<p class="indent">“Guess everybody you meet tries to hit on you.”</p>
<p class="indent">“Not really.”</p>
<p class="indent">Steve Thomas was pitching his heart out. “Hard to meet somebody you
            can really care about, I guess! Somebody who really cares about you.”</p>
<p class="indent">“That’s true everywhere, isn’t it?”</p>
<p class="indent">Sally was beginning to lose patience. She felt idiotic. She looked toward
            Mancuso. He eyed her without emotion.</p>
<p class="indent">“Yeah. Yeah, you’re right,” Steve was saying. He
            raised his glass. “No friends like the old friends, right?”</p>
<p class="indent">“Sure … right.”</p>
<p class="indent">He drank deeply. Sally took a sip of whisky. It was smokey and bitter and
            she wasn’t much of a drinker.</p>
<p class="indent">“Hey, Sal,” Steve said. “Bottoms up.” He
            drained his glass. Just then, Sally needed a drink. She did the same.</p>
<p class="indentt">Upstairs in the hotel room, Ross pushed the desk drawer closed. He looked
            around. What was missing was more interesting than what was there.</p>
<p class="indent">There was no raincoat, no umbrella in the closet. There were no airline
            baggage stubs on the empty suitcase—and the suitcase was too big to be a carryon
            and too big for the few articles of clothing he had found in the dresser. But, most
            important, there was no briefcase, no notebook, no manila folders, no pocket calculator,
            no call-back numbers written on the bedside pad. There were none of the paraphernalia of
            a traveling businessman. Now, he knew what Steve Thomas was not—he was not a
            developer from Colorado. He was not a reporter. Ross looked for clues to what he
            was.</p>
<p class="indent">Quickly, he went to the bed. Someone had been lying there, reading this
            month’s issue of <em class="italic">Penthouse</em>. Ross turned up the
            spread, slipped his hands between the mattress and the box spring. He was running out of
            time and he hadn’t got it done.</p>
<p class="indentt">Sally looked at her watch as the waiter put fresh drinks down before
            them. It was quarter to twelve.</p>
<p class="indent">“Got to make this my last one,” she said. “Some of us
            have to work tomorrow.”</p>
<p class="indent">Steve Thomas was feeling the liquor. He slouched against the <a id="page_87"></a>back of the settee. “Hey, I thought we were just getting
            started.” He raised his glass; some of the liquor splashed on the table.
            “Here’s to long nights.” He drained it, slid over against her, and
            put his hand on her knee. “Hey, look, Sally, what do you say we have a fresh
            round sent upstairs? Maybe talk a while in my room? What do you say?”</p>
<p class="indent">Gently, firmly, Sally removed his hand, “l don’t think
            so.”</p>
<p class="indent">He leaned against her, coaxing, “Come on. Half an hour. What can it
            hurt?”</p>
<p class="indent">She leaned away. “I said, I have to go.” Exasperated, she
            looked down the bar toward Mancuso. He seemed happy enough, ordering another belt,
            chomping on a handful of peanuts. She realized that she had let herself be cozened, had
            let Mancuso use her. Before she realized it, he had conned her into the middle of
            something. She had underestimated Mancuso. He had foxed her. She had to be certain she
            didn’t let that happen again.</p>
<p class="indent">Steve was looking at his watch, trying to read the dial in the half light,
            through the haze of alcohol.</p>
<p class="indent">“Hey, it’s only eleven-thirty,” he said. “You
            can stay till twelve, can’t you?”</p>
<p class="indent">“No, Steve. Five more minutes and I—”</p>
<p class="indent">“What the hell did you come for then?” he said, and she
            could hear the disappointment and irritation in his voice. “Hey? Get me down here
            for—”</p>
<p class="indent">Suddenly, he broke off in midsentence. He stared at her like he was seeing
            her for the first time.</p>
<p class="indent">“Jesus Christ,” he said and pushed the table back abruptly.
            His glass fell over and ice scattered everywhere. He shoved himself up off the couch,
            pushed roughly through the conventioneers, and rushed out of the lounge.</p>
<p class="indent">Sally looked anxiously toward Mancuso.</p>
<p class="indent">Mancuso got down from his stool. He dropped a ten-dollar bill on the bar.
            “Shit,” he said, and followed Steve Thomas out the door.</p>
<p class="indent">When he reached the lobby, Mancuso could see Steve pushing his way toward
            the elevator, shoving through the crowd of conventioneers. Mancuso hurried after him.
            Steve pushed two men aside, grabbed the closing door of the elevator, and pushed his way
            in. The door clapped shut in Mancuso’s face.</p>
<p class="indent">Mancuso turned to the house phones. Conventioneers and wives were using
            every one—and a knot of others were waiting.</p>
<p class="indent">“Fuck,” Mancuso said. He looked at the elevator bank; the <a id="page_88"></a>indicator showed both cars heading up. He turned for the door to
            the fire stairs and ran smack into Sally.</p>
<p class="indent">“I couldn’t help it. He—”</p>
<p class="indent">“Go home,” Mancuso said. “Forget you were
            here.”</p>
<p class="indent">He opened the fire door and began to run up the stairs.</p>
<p class="indentt">There was nothing hidden under the mattress, nothing under the pillows.
            Ross turned the Gideon Bible over and shook it out. Nothing. He threw it back in the
            drawer of the night table and slapped the drawer shut. He checked his watch. Then he
            turned toward the bathroom.</p>
<p class="indentt">Mancuso ran up the gray steel fire stairs, pulling himself along with a
            hand on the railing. His footsteps clanged and rang in the shaftway, and he could hear
            the grating of the elevator, cables through the wall as the car carrying Steve Thomas
            slid smoothly up toward the seventh floor. “Fuck,” he said, under his
            panting breath. “I’ll be a cocksucking, motherfucking, son-of-a-bitching,
            bastard cocksucking son-of-a-bitch …” And his curses punctuated every
            step.</p>
<p class="indentt">Just then Ross clicked on the light in the bathroom. There was a can of
            Rise and an Atra razor on the counter, a tube of Crest, and a toothbrush in one of the
            glasses. On a shelf above the sink, there was a large black-leather toiletries kit. Ross
            picked it up. It was heavy. He shook the kit. Some small metal items rattled around
            inside. Ross unzipped it.</p>
<p class="indentt">Mancuso swung himself around the fourth-floor landing and pounded up the
            steel stairs toward the fifth. He was breathless, panting, his collar sweated to his
            neck. “Motherfucking, shit-eating, cocksucking son-of-a-bitching bastard
            cunt-licking fucking asshole …” He made the turn halfway to the fifth
            floor.</p>
<p class="indentt">The elevator door opened, and Steve Thomas stepped out into the silent
            hallway. He walked quickly to 724. Then he leaned against the door and listened.</p>
<p class="indent">Inside the room, Ross turned out the contents of the shaving kit and they
            clattered on the Formica counter beside the bathroom sink.</p>
<p class="indent">Dimly, almost imperceptibly, Steve heard the sound through the heavy hall
            door. He bent down, drew the .38 caliber revolver <a id="page_89"></a>from his ankle
            holster, took the silencer from his jacket pocket, and eased it over the barrel of the
            gun until it clicked into place. Without making a sound, he slipped the key into the
            lock.</p>
<p class="indent">Ross quickly began to replace the contents of the kit: aftershave,
            deodorant, a bar of Equipage soap in a plastic box, a matchbook sewing kit. All that
            remained on the bathroom counter were five collar stays, two buttons, and four little
            lapel pins.</p>
<p class="indent">He pushed the pins together in a bunch. One was the American flag. The
            other three were a circle, a square, and the letter “S.” Ross stared at
            the pins. Suddenly, his jaw went slack and his eyes opened as if someone had slapped him
            hard across the face. Just as Mancuso pushed, sweating and panting, through the fire
            door at the end of the seventh floor, Steve Thomas silently turned the key in the lock
            and stepped into room 724, and slammed the door behind him.</p>
<p class="indent">“Freeze!” he shouted at Ross.</p>
<p class="indent">Ross wheeled around in time to see a man level a pistol at his chest. With
            all his might, Ross flung the heavy shaving kit backhand across the room. Steve jumped
            back, pulling the gun up. The shaving kit hit the bottom of his wrist and the gun fired.
            There was a soft <em class="italic">pock</em> and a sudden surge of air pressure in
            Ross’s eardrums. The bullet was discharged upward, entered Steve’s neck
            just below his jaw and exploded out the top of his scalp. It blew the cap off his skull
            and splashed blood and tissue on the ceiling. The bullet ricocheted down off the
            concrete under the plaster, up off the concrete under the rug, back down off the
            ceiling, and buried in the mattress. Steve’s body fell onto the bed, writhing and
            twitching, spewing blood on the bedspread, the lamps, and end tables. It jerked in
            gruesome spasms, rolled over, and fell onto the floor.</p>
<p class="indent">Breathless, his shirt sweated to his back and belly, Mancuso reached the
            door of 724. He heard a body hit the floor. Without hesitating, he stepped back, drew
            his revolver, and kicked the door with the flat of his shoe just below the knob. The
            frame holding the striker plate shattered and the door crashed open as Mancuso crouched,
            ready for combat.</p>
<p class="indent">Ross was leaning against the door frame of the bathroom, his hand over his
            eyes. Mancuso stepped into the room, quickly pressing the door closed behind him. At his
            feet, Steve Thomas’s body jerked and quivered on the floor.</p>
<p class="indent">“Jesus Christ,” Mancuso said. Then blood dripped down <a id="page_90"></a>from the ceiling. He ducked out of its way and looked up at the
            spattered tissue on the plaster. “Jesus Christ.”</p>
<p class="indent">Ross was hyperventilating, struggling to catch his breath.
            “Motherfucker … motherfucker … he almost killed me.”</p>
<p class="indent">“Must have lost his head.” Mancuso holstered his pistol,
            took a pair of latex surgical gloves from his pocket, and began to, pull them on.</p>
<p class="indent">“Very funny,” Ross said; catching his breath. He was angry.
            “Where the hell were you?”</p>
<p class="indent">Mancuso loosened his collar where it was sweated to his throat. “I
            had to take the local.” He bent over Steve’s twitching corpse, began
            picking through his pockets. He took Steve’s money and wristwatch.</p>
<p class="indent">“What the hell are you doing?”</p>
<p class="indent">Mancuso patted down all of Steve’s pockets. “No
            wallet,” he said. “You find a wallet?”</p>
<p class="indent">“No.”</p>
<p class="indent">“I’d like to know who this son-of-a-bitch is. And make this
            look like a robbery for the D.C. cops.”</p>
<p class="indent">“Forget it,” Ross said. He pushed himself away from the
            bathroom door and made his way across the room, stepping over and around the spattered
            blood.</p>
<p class="indent">“What are you talking about?” Mancuso said.</p>
<p class="indent">“Forget it, Joe.” Ross stood over him. He opened his hand
            and dropped the four little lapel pins into Mancuso’s waiting palm.</p>
<p class="indent">Mancuso looked down at them: “What the fuck?”</p>
<p class="indent">Then he stopped, and the sweat ran cold into the small of his back. The
            tiny pins glinted on the white latex glove. He looked up at Ross and saw that he, too,
            understood. In Washington, only one kind of man would carry an array of pins like those:
            an agent of the United States Secret Service.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 09_Chapter03.vue?5cc81de8